import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from './api';
import { ExchangeComposer, ReturnAcceptanceCard, ExistingExchange, STAGE_LABEL, STAGE_TONE } from './ReturnsPage';
import { OrderNo } from './OrderNo';
import { detectZebraIfEnabled, deliverLabel, presentPdf } from './labelPrint';
import LabelRunPanel from './LabelRunPanel';
import { fmtWh } from './time';
import { carrierName, TrackLink } from './carriers';
import { ReturnComposer } from './ReturnComposer';
import OrderEditDialog from './OrderEditDialog';
import CustomerCaseBox from './CustomerCaseBox';
import { confirmAndProcess, ProcessResult } from './processOrders';
import { useReprintApproval } from './pp2/ReprintApproval';
import { titleEn, lineEn, customerWords } from './lineName';


// Tab names come from the tabs_config registry via /api/tabs, never from a list
// in here. The hardcoded map this replaces was missing four real tabs — including
// Contact and Product exchange — so an order sitting in either showed its raw key
// on the pill and all through the change log. (Claude, 2026-08-21)

interface Event {
  id: number;
  from_tab: string | null;
  to_tab: string;
  username: string;
  created_at: string;
}

interface Comment {
  id: number;
  comment: string;
  username: string;
  created_at: string;
}

interface LabelRow {
  id: number;
  tracking_number: string;
  // The parcel's SECOND identity where the carrier issues two:
  // Packeta's own Z-code on a resold parcel; Bring's consignment number.
  tracking_alt?: string | null;
  carrier: string;
  printed_at: string | null;
  printed_by?: string | null;
  pickup_point_id?: string | null;
  pickup_point_name?: string | null;
  matched_by?: string | null;
  comment?: string | null;
  comment_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_note?: string | null;
  can_reprint?: boolean;
  /** Output ledger (migration 022): when the label was first sent to a printer,
   *  by whom, how often. printed_at/printed_by above mean BOUGHT. */
  sent_to_printer_at?: string | null;
  sent_to_printer_by?: string | null;
  print_count?: number;
}

interface ContactCorrection {
  id: number; field: string; customer_gave?: string | null; corrected_to?: string | null;
  source: string; username: string; note?: string | null; created_at?: string | null;
}

const CONTACT_FIELDS: { key: string; label: string }[] = [
  { key: 'phone', label: 'Phone' }, { key: 'street_address', label: 'Street address' },
  { key: 'postal_code', label: 'Postal code' }, { key: 'city', label: 'City' },
  { key: 'house_number', label: 'House number' },
  { key: 'first_name', label: 'First name' }, { key: 'last_name', label: 'Last name' },
];

function sourceText(c: ContactCorrection): string {
  if (c.source === 'cs') return 'customer support';
  if (c.source === 'custom_label') return 'custom-label page';
  if (c.source === 'carrier_rejected') return 'refused by the carrier';
  if (c.source === 'shop_customer') return 'shop customer record';
  if (c.source.startsWith('history:')) return `borrowed from ${c.source.slice(8)}`;
  return c.source;
}

interface Detail {
  raw: any;
  tab: string;
  /** Label issue: the label is now bought — print it (if not done) and Process. Stays until the order leaves
   *  Label issue (Fernando 2026-10-09); `printed` = it has been sent to a printer at least once. */
  label_ready?: { carrier: string; tracking: string; label_id: number; printed?: boolean; printed_at?: string | null } | null;
  /** the label failed and there is no label yet: the error, and a pending "ask the Doctor again" */
  label_doctor?: { error: string; relook_requested_at: string | null; relook_requested_by: string | null;
                   /** set while the Doctor is working on THIS order right now */
                   working?: { started_at: string; try: number; of: number;
                               steps: { at: string; text: string }[] } | null;
                   /** the Doctor's latest verdict on this order */
                   last?: { at: string; outcome: string; action: string | null; applied: boolean; note: string } | null } | null;
  /** The customer-facing number (SK4218, NO28091). The WMS id stays the key
   *  everything joins on; this is what people quote. */
  order_number?: string;
  carrier: string;
  webshop_id?: string;
  store_name?: string;
  currency?: string;
  /** when the customer ordered — UTC instant with Z, shown on the warehouse clock */
  order_date?: string | null;
  /** Non-null while an exchange pass runs. `raw` above is always the ORIGINAL
   *  order — the pick list, packing and delivery note carry only the
   *  replacement, which is what this block describes. */
  exchange_pass?: {
    id: number;
    sending: any[];
    replaces: any[];
    sending_lines?: any[];
    replacement_total?: number;
    send_mode?: string;
    no_return?: boolean;
    status?: string;
    pass_started_at?: string | null;
    dispatched_at?: string | null;
  } | null;
  tab_first_times: Record<string, string>;
  events: Event[];
  comments: Comment[];
  not_found_items: string[];
  flag_reason?: any;   // why order_flags pulled the order out of picking (docs/FLAGGED-ORDERS.md)
  /** line id -> how many are missing (set from the packing screen's Issue button) */
  not_found_qty?: Record<string, number>;
  labels?: LabelRow[];
  /** The label "Order label" / the packing scan would print; null = it would BUY.
   *  The server's one rule (_current_live_label): newest live label, inside the
   *  exchange pass, and — on an archived order — bought after the dispatch. */
  live_label_id?: number | null;
  /** Archived orders only: labels bought before this instant left with the parcel. */
  dispatched_at?: string | null;
  /** docs/CONTACT-REPAIR.md — corrections and wrong-marks on the customer's contact data */
  contact_corrections?: ContactCorrection[];
  /** The storefront the customer bought on (backend/order_site.py): customer mails follow it. */
  storefront?: { code: string | null; domain: string | null; language: string | null; label: string; source: string } | null;
  /** 'order' | 'resend' (docs/RESEND-ORDERS.md). A resend is a WMS-created linked
   *  order that sends the goods of `resend_of` again after a lost parcel. */
  kind?: string;
  resend_of?: { order_id: string; order_number: string; tab: string } | null;
  resend?: ResendInfo | null;
  resends?: ResendInfo[];
  /** split shipment: every parcel with lines and timeline; null when the order is one parcel */
  split?: SplitInfo | null;
  active_parcel_id?: number | null;
  /** 'cod' | 'prepaid' | 'unknown' by the store's own method lists */
  payment_kind?: string;
  /** a flagged order the WMS can cancel in its shop (COD on WGR/MyCashflow) — the banner's Delete now */
  flag_delete_possible?: boolean;
  payment_method?: string;
  amount?: number;
  active_exchange_id?: number | null;
  /** returns.return_history: every acceptance of this purchase with stage, items, timestamps, refunds */
  returns?: { acceptances: any[]; already_returned: Record<string, number>; after_return?: any | null } | null;
  /** returns.exchange_history: EVERY exchange of this order from registration on, newest first,
   *  with its derived timeline (Fernando, 2026-09-21: "when we create it there is 100% expectation
   *  that we will send it"). The same reader feeds Returns → Exchange orders. */
  exchanges?: ExchangeInfo[];
  /** packeta_returns.list_for_order: the labelless Packeta return codes issued for this order,
   *  and whether a new one may be created (CZ/SK/HU/RO only). Status is read from the return
   *  parcel's tracking row. (Claude, 2026-09-24) */
  packeta_returns?: { eligible: boolean; why_not: string | null; codes: PacketaReturnRow[] } | null;
  /** Ordered vs shipped (wms_core.shipped_items_of): per line_id what packing actually booked.
   *  `differs` = a resolved packed barcode belongs to another variant than the line. */
  shipped_items?: Record<string, {
    ordered_variation_id?: string | null; ordered_article?: string | null; source: 'packed_items' | 'scan_events';
    differs: boolean;
    packed: { variation_id?: string | null; article_number?: string | null; barcode?: string | null; qty: number;
              manual?: boolean; resolved?: boolean; at?: string | null; by?: string | null }[];
  }>;
}

interface ExchangeItem {
  variation_id: string; qty: number; product_name: string; article_number: string; ean: string;
  product_name_en?: string;       // English (backend/product_names.py, 26.10)
  location: string; picklist: string;
  /** returned items: named by the garment that comes BACK; the line it was sold as alongside */
  wrong_item?: boolean; sold_article?: string; sold_name?: string; back_source?: string | null; display?: string;
  paid_unit?: number | null;      // returned items: what the customer paid per piece
  unit_price?: number | null;     // wanted items: priced as the note prints them
  shop_price?: number | null;     // wanted items: what the shop charges today
  held?: boolean;                 // wanted items: actually reserved in the store's stock system
}
interface ExchangeInfo {
  id: number; status: 'open' | 'processed' | 'released' | 'cancelled' | 'closed' | string;
  send_mode: string; no_return: boolean; reason_text?: string | null;
  created_at?: string | null; created_by?: string | null;
  reserved: boolean; reserve_result?: any;
  returned: ExchangeItem[]; wanted: ExchangeItem[];
  acceptance?: { id: number; accepted_at?: string | null; accepted_by?: string | null; stage?: string; return_order?: any } | null;
  replacement_label?: { id: number; tracking_number: string; carrier: string; created_at?: string; sent_to_printer_at?: string | null; cancelled_at?: string | null } | null;
  money: { paid_returned: number | null; replacement_value: number | null; replacement_shop_value: number | null; difference: number | null };
  returned_at?: string | null; pass_started_at?: string | null; dispatched_at?: string | null;
  processed_at?: string | null; processed_by?: string | null; closed_note?: string | null;
  mid_pass: boolean;
  timeline: { state: string; at: string | null; by?: string | null; detail?: string | null }[];
  current: string; current_at?: string | null; banner: string;
}

/** Timeline wording — the server names the state, this names it for people. */
const EX_STEP: Record<string, string> = {
  registered: 'Registered', reserved: 'Replacement reserved', partly_reserved: 'Partly reserved',
  reservation_failed: 'Nothing reserved', waiting_for_return: 'Waiting for the old item',
  old_item_arrived: 'Old item arrived', polished: 'Old item polished', replenished: 'Old item back on the shelf',
  picking_replacement: 'Replacement being picked', pass_aborted: 'Pass aborted',
  replacement_dispatched: 'Replacement dispatched', awaiting_store_edit: 'Change the order in the store',
  processed: 'Processed', released: 'Released', cancelled: 'Cancelled',
};
const EX_TONE = (status: string, current: string) =>
  status === 'processed' ? 'border-green-900/60 bg-green-950/40 text-green-200'
  : status === 'cancelled' || status === 'released' || status === 'closed' ? 'border-gray-700 bg-gray-800/60 text-gray-300'
  : current === 'pass_aborted' || current === 'reservation_failed' || current === 'partly_reserved' ? 'border-red-900/60 bg-red-950/40 text-red-200'
  : current === 'awaiting_store_edit' ? 'border-indigo-900/60 bg-indigo-950/40 text-indigo-200'
  : 'border-purple-900/60 bg-purple-950/40 text-purple-200';

interface ResendInfo {
  id: number; order_id: string; order_number: string; tab: string;
  status: string;                 // open | dispatched | cancelled
  reason?: string | null; note?: string | null; full?: boolean;
  items?: { line_id: string; variation_id?: string; qty: number; name?: string; code?: string; unit_paid?: number }[];
  created_by?: string; created_at?: string | null; reserved?: boolean;
  dispatched_at?: string | null; tracking_number?: string | null; tracking_carrier?: string | null;
  cancelled_by?: string | null; cancelled_at?: string | null; cancel_note?: string | null;
  /** what it charges again + the money it produced (items, shipping, fee, total…) */
  charges?: Record<string, any> | null;
  /** the customer was told to keep the original (2026-10-03): the waiting-back rows are not expected —
   *  if they come back anyway, no refund (held off sale when the reason is Defect / Damaged in transit) */
  customer_keeps?: boolean;
  /** Tables, not loose text (Fernando, 2026-09-21): what we wait back / what we send,
   *  and the resend's life derived from its timestamps (resends.context_for). */
  waiting_back?: ResendRow[];
  sending?: ResendRow[];
  timeline?: { state: string; at?: string | null; by?: string | null; detail?: string | null }[];
  current?: string; current_at?: string | null; banner?: string;
}
interface ResendRow {
  variation_id: string; qty: number; article_number: string; product_name: string; location: string;
  product_name_en?: string;
  picklist?: string; unit_paid?: number | null; charged?: boolean;
  /** waiting-back rows: the garment that actually left when it differs from the line sold */
  sold_as?: string | null; wrong_item?: boolean; barcode?: string | null; packed_at?: string | null; packed_by?: string | null;
}
/** Split shipments (docs/SPLIT-SHIPMENTS.md): one order, several parcels, per-line
 *  "ship now / later". The order stays what was ordered; a parcel is a view of it. */
interface ParcelItem { line_id: string; qty: number; article_number: string; product_name: string; product_name_en?: string; location: string; unit_price?: number | null }
interface ParcelInfo {
  id: number; parcel_no: number; status: 'open' | 'active' | 'dispatched' | 'cancelled' | string;
  lines: Record<string, number>; items?: ParcelItem[];
  created_at?: string | null; created_by?: string | null; started_at?: string | null; packed_at?: string | null;
  dispatched_at?: string | null; dispatched_by?: string | null; cancelled_at?: string | null; cancelled_by?: string | null;
  /** open parcel whose goods arrived (WGR moved the order out of Waiting for NOS): released at the active parcel's dispatch */
  ready_at?: string | null;
  tracking_number?: string | null; carrier?: string | null; note?: string | null;
  label?: { tracking_number?: string; carrier?: string; sent_to_printer_at?: string | null; cancelled_at?: string | null } | null;
  timeline?: { state: string; at?: string | null; by?: string | null; detail?: string | null }[];
  current?: string; current_at?: string | null; banner?: string;
}
interface SplitInfo {
  parcels: ParcelInfo[]; active_parcel_no: number | null;
  /** open + active parcels still to send; waiting = not yet released; between = one left, next not released */
  open: number; waiting: number; between: boolean;
  /** parcels that ship or shipped (cancelled not counted); badge = dispatched / total */
  total: number; badge: string; active_items?: number | null;
}
const PC_STEP: Record<string, string> = {
  created: 'Created', waiting: 'Waiting', ready: 'Goods arrived', active: 'Being picked and packed', label_bought: 'Label bought',
  label_printed: 'Label printed', packed: 'Packed', dispatched: 'Dispatched', cancelled: 'Cancelled',
};

const RS_STEP: Record<string, string> = {
  created: 'Created', reserved: 'Stock reserved', not_reserved: 'Not fully reserved', label_bought: 'Label bought',
  label_failed: 'Label failed', note_printed: 'Delivery note printed', packed: 'Packed', dispatched: 'Dispatched', cancelled: 'Cancelled',
};

interface Props {
  orderId: string;
  onClose: () => void;
  onMoved?: () => void;                      // order left this tab — closes the panel
  onProcessed?: (result: ProcessResult) => void;   // Process accepted — the page may show the round recap
  onChanged?: () => void;                    // order changed in place — panel stays open
  onOpenOrder?: (id: string) => void;        // show another order in this panel (parent ⇄ resend)
  tabs?: { key: string; label: string }[];   // the tabs_config registry
  /** a P&P station / a Tier 1 person (21.10): they look; every change is refused by the server (device_guard) */
  lookOnly?: boolean;
}

interface PacketaReturnRow {
  id: number; packet_id: string; barcode: string; barcode_text: string; drop_off_code: string;
  number_sent: string; sender: string; consign_country: string; currency: string; reason?: string | null;
  status: 'open' | 'on_its_way' | 'arrived' | 'cancelled' | 'exception' | 'unknown';
  tracking_state?: string | null; last_event_at?: string | null;
  created_by: string; created_at: string;
  cancelled_by?: string | null; cancelled_at?: string | null; cancel_note?: string | null;
}

const RC_TONE: Record<string, string> = {
  open: 'bg-blue-900/50 text-blue-300', on_its_way: 'bg-amber-900/40 text-amber-300',
  arrived: 'bg-green-900/40 text-green-300', cancelled: 'bg-gray-800 text-gray-500',
  exception: 'bg-red-900/40 text-red-300', unknown: 'bg-red-900/40 text-red-300',
};

/** Packeta return codes (Claude, 2026-09-24): a CZ/SK/HU/RO customer says the 9-digit code at a
 *  Packeta branch that accepts returns — no label. One open code is the rule; another needs a
 *  reason. The WMS sends no e-mail: CS passes the code on. Same core as the MCP tool. */
function PacketaReturnCard({ orderId, block, onChanged }: {
  orderId: string;
  block: { eligible: boolean; why_not: string | null; codes: PacketaReturnRow[] };
  onChanged: () => void;
}) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const codes = block.codes || [];
  const open = codes.find(c => c.status === 'open');
  if (codes.length === 0 && !block.eligible) return null;
  const post = async (path: string, body: any) => {
    setErr(''); setBusy(true);
    try {
      const res = await authFetch(`${API}/returns/packeta/${path}`, { method: 'POST', body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Packeta return code failed')); return; }
      onChanged();
    } finally { setBusy(false); }
  };
  const create = () => {
    if (open) {
      const reason = window.prompt(
        `An open return code already exists (${open.drop_off_code}).\n\nWhy is another one needed? (required — e.g. defect handling, second parcel)`);
      if (reason === null) return;
      if (!reason.trim()) { setErr('A reason is required for an additional code.'); return; }
      post(encodeURIComponent(orderId), { another: true, reason: reason.trim() });
    } else {
      post(encodeURIComponent(orderId), {});
    }
  };
  const cancel = (c: PacketaReturnRow) => {
    const note = window.prompt(
      `Cancel return code ${c.barcode_text}?\n\nThe customer can no longer use ${c.drop_off_code}.\n\nNote (optional)`);
    if (note === null) return;
    post(`${encodeURIComponent(c.packet_id)}/cancel`, { note: note.trim() || null });
  };
  const copy = async (c: PacketaReturnRow) => {
    try { await navigator.clipboard.writeText(c.drop_off_code); setCopied(c.packet_id); setTimeout(() => setCopied(null), 1500); } catch { /* clipboard blocked */ }
  };
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2.5">
        <span className="text-white text-sm font-semibold">Packeta return codes</span>
        {block.eligible && (
          <button disabled={busy} onClick={create}
            className="text-xs px-3 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50">
            {open ? 'Another code…' : 'Create return code'}
          </button>
        )}
      </div>
      {codes.length === 0 && (
        <div className="px-4 py-2 text-xs text-gray-500">
          No return code yet. The customer says the code at a Packeta branch that accepts returns — no label needed.
        </div>
      )}
      {codes.map(c => (
        <div key={c.packet_id} className="px-4 py-2 border-t border-gray-800/60 text-xs">
          <div className="flex items-center gap-2">
            <span className={`font-mono text-base tracking-wider ${c.status === 'cancelled' ? 'text-gray-500 line-through' : 'text-white'}`}>
              {c.drop_off_code}
            </span>
            <button onClick={() => copy(c)} className="px-2 py-0.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300">
              {copied === c.packet_id ? 'Copied' : 'Copy'}
            </button>
            <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${RC_TONE[c.status] || RC_TONE.unknown}`}>
              {c.status.replace(/_/g, ' ')}
            </span>
            {c.status === 'open' && (
              <button disabled={busy} onClick={() => cancel(c)}
                className="ml-auto px-2 py-0.5 rounded bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-300">
                Cancel
              </button>
            )}
          </div>
          <div className="text-gray-400 mt-0.5">
            parcel <span className="font-mono">{c.barcode_text}</span> · {c.consign_country} · sender {c.sender}
          </div>
          <div className="text-gray-500 mt-0.5">
            {c.created_by} · {fmtWh(c.created_at, 'short')}{c.reason ? ` · ${c.reason}` : ''}
          </div>
          {c.cancelled_at && (
            <div className="text-gray-500 mt-0.5">
              cancelled {fmtWh(c.cancelled_at, 'short')}{c.cancelled_by ? ` · ${c.cancelled_by}` : ''}
              {c.cancel_note ? <span className="text-red-400/80"> — {c.cancel_note}</span> : null}
            </div>
          )}
        </div>
      ))}
      {err && <div className="px-4 py-2 text-xs text-amber-300">{err}</div>}
    </div>
  );
}

/** "Return…" (right-hand button, Fernando 27.10 — was the bottom "Return / exchange" card): the customer announces a
 *  return. Loads the same lookup the Accept page uses and renders ReturnComposer with "the goods are here" unticked;
 *  each piece is marked Refund / Exchange for… / Nothing. Sending something again lives under "Send again". */
function ReturnDialog({ orderId, onChanged, onClose }: { orderId: string; onChanged: () => void; onClose: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch(`${API}/returns/lookup/${encodeURIComponent(orderId)}?by=id`)
      .then(async res => {
        if (!res.ok) { const e = await res.json().catch(() => ({})); setErr(typeof e.detail === 'string' ? e.detail : (e.detail?.message || 'Could not load the order for a return')); return; }
        setData(await res.json());
      }).catch(() => setErr('No connection to the WMS'));
  }, [orderId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6" onClick={onClose}>
      <div className="bg-[#0f1117] border border-gray-800 rounded-xl p-4 w-full max-w-4xl max-h-[92vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">Return</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white">✕</button>
        </div>
        {err && <div className="text-sm text-amber-300">{err}</div>}
        {!data && !err && <div className="text-sm text-gray-500">Loading the order…</div>}
        {data && <ReturnComposer key={orderId} order={data} arrivedDefault={false} compact
          onDone={() => { onChanged(); onClose(); }} onCancel={onClose} />}
      </div>
    </div>
  );
}

/** The original parcel in a few facts — what "Send again" shows first (Fernando 27.10: "delivered or in delivery and
 *  last update so a person processing it can act if something seems off"). Shown, never a block. */
function TrackingFacts({ s }: { s: any | null }) {
  if (!s) return <div className="rounded-lg border border-gray-800 px-3 py-2 text-xs text-gray-500">No carrier tracking for this order's parcel.</div>;
  const tone = s.delivered ? 'border-green-900/60 bg-green-950/30 text-green-100'
    : s.state === 'returned' || s.state === 'returning' || (s.seems_off || []).length ? 'border-red-900/60 bg-red-950/30 text-red-100'
    : 'border-amber-900/60 bg-amber-950/30 text-amber-100';
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm space-y-0.5 ${tone}`}>
      <div><span className="font-semibold">The original parcel: {s.state_text}</span>
        <span className="opacity-70 text-xs ml-2">{s.carrier} {s.tracking_number}</span></div>
      {s.delivered && s.delivered_at && <div className="text-xs">Delivered {fmtWh(s.delivered_at, 'datetime')} — check with the customer before sending anything again.</div>}
      {!s.delivered && (
        <div className="text-xs">
          Last update: {s.last_update_at ? `${fmtWh(s.last_update_at, 'datetime')} (${s.days_since_update === 0 ? 'today' : `${s.days_since_update} day${s.days_since_update === 1 ? '' : 's'} ago`})` : 'none yet'}
          {s.last_update_text ? ` — ${s.last_update_text}` : ''}
          {s.moving && s.days_since_update !== null && s.days_since_update < 3 ? ' · it is still moving' : ''}
        </div>
      )}
      {(s.seems_off || []).length > 0 && <div className="text-xs font-semibold">Seems off: {s.seems_off.join(' · ')}</div>}
    </div>
  );
}

/** One resend as a table (Fernando, 2026-09-21: "the loose text is not as organised and
 *  serious as a proper table"): the rows we WAIT BACK, the rows we SEND, its banner and
 *  timeline. Shown on the original (one per resend) and on the resend order itself. */
function ResendTable({ r, side, onOpenOrder, money, tabLabel, canCancel, onCancel, cancelling }: {
  r: ResendInfo; side: 'parent' | 'resend'; onOpenOrder?: (id: string) => void;
  money: (v: number) => string; tabLabel: (k: string | null | undefined) => string;
  canCancel?: boolean; onCancel?: () => void; cancelling?: boolean;
}) {
  const priceOf = (v: number | null | undefined) => (v === null || v === undefined ? '—' : money(Number(v)));
  const tone = r.status === 'dispatched' ? 'border-green-900/60 bg-green-950/40 text-green-200'
    : r.status === 'cancelled' ? 'border-gray-700 bg-gray-800/60 text-gray-300'
    : r.current === 'not_reserved' || r.current === 'label_failed' ? 'border-red-900/60 bg-red-950/40 text-red-200'
    : 'border-amber-900/60 bg-amber-950/40 text-amber-200';
  return (
    <div className="px-4 py-3 text-sm space-y-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        {side === 'parent'
          ? <button onClick={() => onOpenOrder?.(r.order_id)} className="text-white font-medium underline decoration-dotted hover:text-amber-100">{r.order_number}</button>
          : <span className="text-white font-medium">Resend #{r.id}</span>}
        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
          r.status === 'dispatched' ? 'bg-green-900/60 text-green-300' : r.status === 'cancelled' ? 'bg-gray-700 text-gray-400' : 'bg-amber-900/60 text-amber-200'}`}>
          {r.status === 'open' ? `open — in ${tabLabel(r.tab)}` : r.status}
        </span>
        <span className="text-xs text-gray-500">
          created {r.created_at ? fmtWh(r.created_at, 'short') : ''} by {r.created_by}
          {r.charges && <> · charges {[r.charges.items && 'items', r.charges.shipping && 'shipping', r.charges.fee && 'fee'].filter(Boolean).join(', ') || 'nothing'}{r.charges.total != null ? `: ${money(Number(r.charges.total))}` : ''}</>}
        </span>
        {canCancel && r.status === 'open' && (
          <button onClick={onCancel} disabled={cancelling}
            className="ml-auto text-xs px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-red-200 disabled:opacity-40">
            {cancelling ? 'Cancelling…' : 'Cancel resend'}
          </button>
        )}
      </div>
      {r.reason && <div className="text-xs text-gray-400">{r.reason}{r.note ? <span className="text-gray-500"> — {r.note}</span> : null}</div>}
      {r.banner && (
        <div className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-3 ${tone}`}>
          <span>{r.banner}</span>
          {r.current_at && <span className="text-xs opacity-70 whitespace-nowrap">{fmtWh(r.current_at, 'short')}</span>}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="px-3 py-1.5 text-left"></th>
              <th className="px-3 py-1.5 text-left">Article #</th>
              <th className="px-3 py-1.5 text-left">Location</th>
              <th className="px-3 py-1.5 text-left">Product</th>
              <th className="px-3 py-1.5 text-center">Qty</th>
              <th className="px-3 py-1.5 text-right">Paid / charged</th>
            </tr>
          </thead>
          <tbody>
            {(r.waiting_back || []).map((w, n) => (
              <tr key={`w${n}`} className="border-b border-gray-800/60">
                <td className={`px-3 py-1.5 text-[10px] uppercase whitespace-nowrap ${r.customer_keeps ? 'text-teal-300' : 'text-gray-500'}`}
                    title={r.customer_keeps ? 'The customer was told to keep it — not expected back. If it comes back anyway it is not refunded.' : undefined}>
                  {r.customer_keeps ? 'kept by the customer' : 'waiting back'}
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{w.article_number || '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{w.location || '—'}</td>
                <td className="px-3 py-1.5 text-gray-200">
                  {titleEn(w) || '?'}
                  {w.wrong_item && (
                    <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200"
                          title={`Packing scanned barcode ${w.barcode || '?'}${w.packed_at ? ` on ${fmtWh(w.packed_at, 'short')}` : ''}${w.packed_by ? ` by ${w.packed_by}` : ''}`}>
                      shipped instead of {w.sold_as}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center text-gray-200">{w.qty}</td>
                <td className="px-3 py-1.5 text-right text-gray-500">—</td>
              </tr>
            ))}
            {(r.sending || []).map((s, n) => (
              <tr key={`s${n}`} className="border-b border-gray-800/60 last:border-0">
                <td className="px-3 py-1.5 text-[10px] uppercase text-amber-300 whitespace-nowrap">sending</td>
                <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{s.article_number || '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-blue-400">{s.location || '—'}</td>
                <td className="px-3 py-1.5 text-white" title={customerWords(s)}>{titleEn(s) || '?'}</td>
                <td className="px-3 py-1.5 text-center text-white">{s.qty}</td>
                <td className="px-3 py-1.5 text-right text-gray-300">
                  {s.charged ? priceOf(s.unit_paid) : <span className="text-gray-500">not charged{s.unit_paid != null ? ` (paid ${priceOf(s.unit_paid)})` : ''}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(r.timeline || []).length > 0 && (
        <div className="grid gap-x-4 gap-y-0.5 text-xs" style={{ gridTemplateColumns: 'auto auto 1fr' }}>
          {(r.timeline || []).map((t, n) => {
            const isCur = t.state === r.current;
            return (
              <React.Fragment key={n}>
                <span className={`whitespace-nowrap ${isCur ? 'text-white font-medium' : 'text-gray-500'}`}>{isCur ? '▸ ' : '· '}{RS_STEP[t.state] || t.state}</span>
                <span className={`whitespace-nowrap ${isCur ? 'text-gray-300' : 'text-gray-600'}`}>{t.at ? fmtWh(t.at, 'short') : '—'}{t.by ? ` · ${t.by}` : ''}</span>
                <span className={isCur ? 'text-gray-400' : 'text-gray-600'}>{t.detail || ''}</span>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionHead({ title }: { title: string }) {
  return (
    <div className="bg-gray-800 px-4 py-2.5 text-white text-sm font-semibold">
      {title}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex border-b border-gray-800 last:border-0">
      <div className="w-28 md:w-44 px-3 md:px-4 py-2.5 text-gray-400 text-sm flex-none">{label}</div>
      <div className="flex-1 min-w-0 break-words px-3 md:px-4 py-2.5 text-white text-sm">{value}</div>
    </div>
  );
}

// Every instant in this panel on the warehouse clock (time.ts). The old
// version parsed the DB's naive-UTC strings as browser-local, so events,
// comments and label times showed three hours early. (Fernando, 2026-09-08)
function fmt(dt: string | null | undefined) {
  if (!dt) return null;
  return fmtWh(dt, 'seconds') || null;
}

export default function OrderDetailPanel({ orderId, onClose, onMoved, onProcessed, onChanged, onOpenOrder, tabs, lookOnly }: Props) {
  const tabLabel = (key: string | null | undefined) =>
    (key && (tabs || []).find(t => t.key === key)?.label) || key || '';

  const [detail, setDetail] = useState<Detail | null>(null);
  // a reprint (note or label printed before): Tier 1 needs a manager's PIN or badge (Fernando 19.10)
  const reprintApproval = useReprintApproval();
  // Resend orders (Fernando, 2026-09-09): a lost parcel is sent again as a NEW
  // linked order. Manager/admin only — a resend gives goods away for free.
  const role = localStorage.getItem('wms_role') || 'staff';
  // the exchange being edited in the composer below (Edit on the pass card)
  const [editExchange, setEditExchange] = useState<ExistingExchange | null>(null);
  const cancelExchange = async (exId: number) => {
    const note = window.prompt(
      `Cancel exchange E${exId}?\n\n• the reserved replacement goes back on sale\n• the order leaves the exchange pass and returns to the tab it came from\n• the exchange is closed\n\nWhy is it not going ahead? (a note is required when nothing is reserved)`);
    if (note === null) return;
    const res = await authFetch(`${API}/returns/exchange-requests/${exId}/cancel`,
      { method: 'POST', body: JSON.stringify({ note: note.trim() || null, abort_pass: true }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { alert(typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Could not cancel the exchange')); return; }
    if (d.ok === false) alert('Part of the stock was not given back, so the exchange stays open — see Returns → Exchange orders.');
    load();
  };
  const canResend = role === 'admin' || role === 'manager';
  // Process from the order page (Fernando, 2026-09-24) — the same confirm and call as the
  // list's "Process orders" (processOrders.ts), so the desk needs no trip back to the list.
  const [processingOne, setProcessingOne] = useState(false);
  const [askingDoctor, setAskingDoctor] = useState(false);
  const doctorBusy = !!(detail?.label_doctor && (detail.label_doctor.working || detail.label_doctor.relook_requested_at));
  useEffect(() => {
    if (!doctorBusy) return;
    const t = setInterval(() => { load(); }, 10000);   // follow the Doctor while it works
    return () => clearInterval(t);
  }, [doctorBusy, orderId]);  // eslint-disable-line
  // "Ask the Label Doctor again" (2026-09-24): the Doctor looks at unfixed orders daily;
  // this sends one back now. The fix stays the Doctor's — this only asks.
  const askDoctor = async () => {
    if (askingDoctor) return;
    setAskingDoctor(true);
    try {
      const r = await authFetch(`${API}/orders/${orderId}/label-doctor/relook`, { method: 'POST', body: '{}' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert(d.detail || 'Could not ask the Label Doctor'); return; }
      await load(); onChanged?.();
    } finally { setAskingDoctor(false); }
  };
  const canProcess = role !== 'viewer';
  const processThis = async () => {
    if (!detail || processingOne) return;
    setProcessingOne(true);
    try {
      const ok = await confirmAndProcess([{
        id: orderId, order_number: detail.order_number,
        split: detail.split ? { active_parcel_no: detail.split.active_parcel_no,
                                waiting: detail.split.waiting, between: detail.split.between } : null,
        labelUnprinted: !!detail.label_ready && !detail.label_ready.printed }]);
      if (ok) { onProcessed?.(ok); onMoved ? onMoved() : onClose(); }
    } finally { setProcessingOne(false); }
  };
  // Split shipments (Fernando, 2026-09-21): manager/admin mark lines to ship later.
  const canSplit = canResend;
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitLater, setSplitLater] = useState<Record<string, number>>({});
  const [splitNote, setSplitNote] = useState('');
  const [splitBusy, setSplitBusy] = useState(false);
  const postParcel = async (path: string, body?: any) => {
    setSplitBusy(true);
    try {
      const r = await authFetch(`${API}/orders/${orderId}/${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // a 422 from the body model comes as a list of {loc, msg}; say the fields, not "[object Object]"
        const det = d.detail;
        const msg = typeof det === 'string' ? det
          : Array.isArray(det) ? det.map((e: any) => `${(e.loc || []).slice(1).join('.')}: ${e.msg}`).join('\n')
          : (det?.message || `HTTP ${r.status}`);
        alert(msg); return false;
      }
      return true;
    } finally { setSplitBusy(false); }
  };
  const submitSplit = async () => {
    const later = Object.entries(splitLater).filter(([, q]) => q > 0).map(([line_id, qty]) => ({ line_id, qty }));
    if (later.length === 0) { alert('Mark at least one line to ship later.'); return; }
    if (await postParcel('split', { later, note: splitNote || null })) {
      setSplitOpen(false); setSplitLater({}); setSplitNote(''); await load(); onChanged?.();
    }
  };
  const releaseParcel = async (no: number) => {
    if (!window.confirm(`Release parcel ${no}? The order goes back to New to be picked, packed and labelled — its label is bought by the sweep, and the delivery note must be printed again (it now shows parcel ${no}). The shop hears nothing yet.`)) return;
    if (await postParcel(`parcels/${no}/release`)) { await load(); onChanged?.(); }
  };
  const cancelParcel = async (no: number, active: boolean) => {
    const note = window.prompt(`Cancel parcel ${no} — why are these lines not being sent? (required; edit the shop order accordingly)`
      + (active ? '\n\nThis parcel is being picked/packed right now: its shipping label is cancelled too.' : ''));
    if (!note) return;
    if (await postParcel(`parcels/${no}/cancel`, { note })) { await load(); onChanged?.(); }
  };
  const cancelSplit = async () => {
    if (!window.confirm('Cancel the split? The order ships as one parcel again; the parcel label is cancelled and a whole-order label is bought.')) return;
    if (await postParcel('split/cancel')) { await load(); onChanged?.(); }
  };
  // Cancel split only while every parcel is still open/active (nothing left, nothing
  // cancelled) and the order is in a working tab — the server refuses otherwise
  const canCancelSplit = !!detail?.split && detail.split.parcels.length > 0
    && detail.split.parcels.every(p => p.status === 'open' || p.status === 'active')
    && !['archived', 'deleted', 'non_completed'].includes(detail.tab);
  // Delete order (Fernando, 2026-09-17): cancel in the shop + refund the customer. MCF only for now.
  const canDelete = canResend;
  // Refund override dialog (2026-09-29): the payment position + the form
  const [refOpen, setRefOpen] = useState(false);
  const [refPos, setRefPos] = useState<any | null>(null);
  const [refErr, setRefErr] = useState('');
  const [refPick, setRefPick] = useState<Record<string, string>>({});   // component key → amount typed ('' = unticked)
  const [refFree, setRefFree] = useState('');
  const [refReason, setRefReason] = useState('Not the customer\'s fault');
  const [refNote, setRefNote] = useState('');
  const [refBusy, setRefBusy] = useState(false);
  const [refResult, setRefResult] = useState<any | null>(null);
  const loadRefundPosition = async () => {
    setRefErr('');
    const r = await authFetch(`${API}/returns/orders/${encodeURIComponent(orderId)}/refund-position`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setRefErr(d.detail || `HTTP ${r.status}`); return; }
    setRefPos(d);
  };
  const refTotal = () => {
    const parts = Object.values(refPick).reduce((s, v) => s + (Number(v) || 0), 0);
    return Math.round((parts + (Number(refFree) || 0)) * 100) / 100;
  };
  const submitRefund = async () => {
    if (!refPos) return;
    const components = Object.entries(refPick).filter(([, v]) => Number(v) > 0)
      .map(([k, v]) => ({ label: (refPos.components || []).find((c: any) => keyOf(c) === k)?.label || k, amount: Number(v) }));
    if (Number(refFree) > 0) components.push({ label: 'Other', amount: Number(refFree) });
    const amount = refTotal();
    if (amount <= 0) return;
    if (!window.confirm(`Refund ${amount.toFixed(2)} ${refPos.currency} to the customer of order ${detail?.order_number || orderId}?\n\n${refReason}${refNote ? ` — ${refNote}` : ''}\n\n${refPos.open_job ? 'It is added to the pending return refund (one payment to the customer).' : refPos.auto_refund && refPos.has_transaction ? 'It is paid through Paytrail by the system.' : 'It waits in Returns → Manual refunds to be paid by hand.'}`)) return;
    setRefBusy(true); setRefErr('');
    const r = await authFetch(`${API}/returns/orders/${encodeURIComponent(orderId)}/refund`, {
      method: 'POST', body: JSON.stringify({ amount, reason: refReason, note: refNote, components }) });
    const d = await r.json().catch(() => ({}));
    setRefBusy(false);
    if (!r.ok) { setRefErr(d.detail || `HTTP ${r.status}`); return; }
    setRefResult(d); setRefPos(d.position); setRefPick({}); setRefFree('');
    onChanged?.();
  };
  const keyOf = (c: any) => `${c.key}:${c.label}`;
  const [delOpen, setDelOpen] = useState(false);
  const [delNote, setDelNote] = useState('');
  const [delEmail, setDelEmail] = useState(true);
  const [delBusy, setDelBusy] = useState(false);
  const [delResult, setDelResult] = useState<any | null>(null);
  const submitDelete = async (recover: 'all' | 'none') => {
    if (!delNote.trim()) return;
    setDelBusy(true);
    try {
      const r = await authFetch(`${API}/orders/${orderId}/cancel-and-refund`, {
        method: 'POST', body: JSON.stringify({ note: delNote.trim(), send_email: delEmail, recover }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setDelResult({ error: (typeof d.detail === 'string' ? d.detail : d.detail?.message) || `Could not cancel (HTTP ${r.status})` }); return; }
      setDelResult(d);
    } catch (e: any) {
      setDelResult({ error: e.message || String(e) });
    } finally { setDelBusy(false); }
  };
  const [resendOpen, setResendOpen] = useState(false);
  // Edit order (2026-10-05): change a line's size / quantity / remove it, in the shop itself
  const [editOpen, setEditOpen] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [resendReasons, setResendReasons] = useState<{ id: number; label: string }[]>([]);
  const [resendPick, setResendPick] = useState<Record<string, number>>({});   // line id -> qty (0 = not sent)
  const [resendReasonId, setResendReasonId] = useState<number | ''>('');
  const [resendReasonText, setResendReasonText] = useState('');
  const [resendNote, setResendNote] = useState('');
  // what the resend charges again — all on by default ("general resend means we
  // recharge for everything", Fernando 2026-09-09), each row switchable
  const [charge, setCharge] = useState({ items: true, shipping: true, fee: true });
  // the customer keeps the original (Fernando, 2026-10-03): nothing expected back; if it comes
  // back anyway it is not refunded — held off sale when the reason is Defect / Damaged in transit
  const [resendKeeps, setResendKeeps] = useState(false);
  // the Defect hint opens the Return / exchange composer (same size) instead
  // "Send again" (Fernando 27.10): one button — Resend (the same items) or Exchange (something else, or the same item
  // tied to the lost one); and "Return…" for an announced return (the old bottom card)
  const [sendAgain, setSendAgain] = useState<null | 'choose' | 'exchange'>(null);
  const [sendAgainReason, setSendAgainReason] = useState('');
  const [returnOpen, setReturnOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendResult, setResendResult] = useState<any | null>(null);
  const [cancellingResend, setCancellingResend] = useState(false);

  const lineIdOf = (p: any, i: number) => String(p.id || p.variation_id || p.product_code || '') || `row-${i}`;

  const openResend = async () => {
    setResendResult(null);
    const lines = ((detail?.raw?.products) || []).filter((p: any) => (p.quantity ?? 0) > 0);
    const pick: Record<string, number> = {};
    lines.forEach((p: any, i: number) => { pick[lineIdOf(p, i)] = Number(p.quantity || 0); });
    setResendPick(pick);
    setResendReasonId(''); setResendReasonText(''); setResendNote(''); setResendKeeps(false);
    setCharge({ items: true, shipping: true, fee: true });
    try {
      const r = await authFetch(`${API}/returns/reasons?kind=resend&active_only=true`);
      if (r.ok) setResendReasons(((await r.json()).reasons || []).map((x: any) => ({ id: x.id, label: x.label })));
    } catch { /* the free-text field still works */ }
    setResendOpen(true);
  };

  const submitResend = async () => {
    if (!detail || resendBusy) return;
    const lines = ((detail.raw?.products) || []).filter((p: any) => (p.quantity ?? 0) > 0);
    const items: { line_id: string; qty: number }[] = lines
      .map((p: any, i: number) => ({ line_id: lineIdOf(p, i), qty: resendPick[lineIdOf(p, i)] || 0 }))
      .filter((x: { line_id: string; qty: number }) => x.qty > 0);
    if (!items.length) { alert('Choose at least one item to re-send.'); return; }
    if (!resendReasonId && !resendReasonText.trim()) { alert('Choose or type a reason.'); return; }
    const all = items.length === lines.length && lines.every((p: any, i: number) => (resendPick[lineIdOf(p, i)] || 0) === Number(p.quantity || 0));
    setResendBusy(true);
    try {
      const r = await authFetch(`${API}/orders/${orderId}/resend`, {
        method: 'POST',
        body: JSON.stringify({
          items: all ? null : items,
          reason_id: resendReasonId || null,
          reason_text: resendReasonText.trim() || null,
          note: resendNote.trim() || null,
          charge_items: charge.items, charge_shipping: charge.shipping, charge_fee: charge.fee,
          customer_keeps: resendKeeps,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setResendResult({ error: d.detail || 'The resend could not be created.' }); return; }
      setResendResult(d);
      load(); onChanged?.();
    } finally { setResendBusy(false); }
  };

  const cancelResend = async () => {
    if (!detail?.resend || cancellingResend) return;
    const note = window.prompt(
      `Cancel resend ${detail.order_number || orderId}?\n\nThe reserved stock is released and the order moves to Deleted. Why?`);
    if (note === null) return;
    if (!note.trim()) { alert('A note is required.'); return; }
    setCancellingResend(true);
    try {
      const r = await authFetch(`${API}/orders/${orderId}/resend/cancel`, {
        method: 'POST', body: JSON.stringify({ note: note.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert(d.detail || 'Could not cancel the resend.'); return; }
      alert(d.released ? 'Resend cancelled — the reservation is released.' : 'Resend cancelled — the reservation could NOT be fully released, see Review.');
      onMoved?.();
    } finally { setCancellingResend(false); }
  };
  // Amounts in the ORDER's currency. Every amount here was printed with a
  // hard-coded "€" while the grid said 55660.00 HUF for the same order
  // (HU3299, Fernando 2026-09-08). Same rule as the delivery note: € for
  // euros, the ISO code for everything else.
  const money = (v: number | string | null | undefined) => {
    const n = Number(v ?? 0);
    const cur = (detail?.currency || 'EUR').toUpperCase();
    return cur === 'EUR' ? `${n.toFixed(2)} €` : `${n.toFixed(2)} ${cur}`;
  };
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [printingNote, setPrintingNote] = useState(false);
  const [notFound, setNotFound] = useState<Set<string>>(new Set());
  const [moving, setMoving] = useState(false);
  // Print shipping label: the two-option popup, and this panel's own label run
  // (the same job flow as the grid's Print labels, with a one-order batch).
  const [labelMenu, setLabelMenu] = useState(false);
  const [startingRun, setStartingRun] = useState(false);
  const [labelRun, setLabelRun] = useState<{ jobId: string; zebra: any } | null>(null);
  const navigate = useNavigate();

  const startOrderLabel = async () => {
    if (startingRun) return;
    setStartingRun(true);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await reprintApproval.run(approver => authFetch(`${API}/orders/print-labels/start`, {
        method: 'POST', body: JSON.stringify({ ids: [orderId], format: zebra ? 'zpl' : 'pdf', ...approver }),
      }));
      if (!res) return;                                  // the reprint approval was cancelled
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Label generation could not be started'); return; }
      setLabelMenu(false);
      setLabelRun({ jobId: data.job_id, zebra });
    } finally { setStartingRun(false); }
  };

  const load = () =>
    authFetch(`${API}/orders/${orderId}/detail`)
      .then(r => r.json())
      .then(setDetail);

  // Parcel tracking (backend/tracking.py): where each of this order's parcels
  // physically is. Best-effort — a missing tracking row shows nothing.
  const [parcels, setParcels] = useState<any[]>([]);
  const [trackSummary, setTrackSummary] = useState<any | null>(null);
  // per parcel: the newest 5 events, or all of them
  const [allEvents, setAllEvents] = useState<Record<number, boolean>>({});
  const loadParcels = () =>
    authFetch(`${API}/tracking/order/${orderId}`)
      .then(async r => { if (r.ok) { const d = await r.json(); setParcels(d.parcels || []); setTrackSummary(d.summary || null); } })
      .catch(() => {});
  useEffect(() => { loadParcels(); }, [orderId]);
  // which Packeta handover / HRX shipment took this order's parcel (3.11: "We should see what went with which order")
  const [handovers, setHandovers] = useState<{ id: number; kind: string; code: string; ship_date: string | null; status: string;
                                               by_person: string | null; at: string | null; unscanned: boolean }[]>([]);
  useEffect(() => {
    authFetch(`${API}/bulks/of-order/${orderId}`).then(async r => { if (r.ok) setHandovers((await r.json()).bulks || []); }).catch(() => {});
  }, [orderId]);

  useEffect(() => { load(); }, [orderId]);
  useEffect(() => {
    if (detail) setNotFound(new Set((detail.not_found_items || []).map(String)));
  }, [detail]);

  const saveComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    await authFetch(`${API}/orders/${orderId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
    setComment('');
    await load();
    setSaving(false);
  };

  const [editingLabel, setEditingLabel] = useState<number | null>(null);
  const [labelComment, setLabelComment] = useState('');

  // Customer support's mark: the customer gave this detail wrong (docs/CONTACT-REPAIR.md).
  const [markWrong, setMarkWrong] = useState<{ field: string; value: string; note: string } | null>(null);
  const [savingMark, setSavingMark] = useState(false);
  const saveMarkWrong = async () => {
    if (!markWrong) return;
    setSavingMark(true);
    const r = await authFetch(`${API}/orders/${orderId}/contact-corrections`, {
      method: 'POST',
      body: JSON.stringify({ field: markWrong.field, corrected_to: markWrong.value, note: markWrong.note }),
    });
    setSavingMark(false);
    if (r.ok) { setMarkWrong(null); await load(); }
    else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Could not record it'); }
  };

  const saveLabelComment = async () => {
    if (editingLabel == null) return;
    await authFetch(`${API}/labels/${editingLabel}/comment`, {
      method: 'POST',
      body: JSON.stringify({ comment: labelComment }),
    });
    setEditingLabel(null);
    setLabelComment('');
    await load();
  };

  const [busyLabel, setBusyLabel] = useState<number | null>(null);

  // Reprint the exact same label (no new shipment) — same Zebra/PDF path as
  // bulk printing. The window is opened synchronously for the browser gesture.
  const reprintLabel = async (labelId: number) => {
    if (busyLabel != null) return;
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing reprint…</p>');
    setBusyLabel(labelId);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await reprintApproval.run(approver => authFetch(`${API}/labels/${labelId}/reprint`, {
        method: 'POST', body: JSON.stringify({ format: zebra ? 'zpl' : 'pdf', ...approver }),
      }));
      if (!res) { if (win) win.close(); return; }        // the reprint approval was cancelled
      const data = await res.json();
      if (!res.ok) { if (win) win.close(); alert(data.detail || 'Reprint failed'); return; }
      await deliverLabel(win, data, zebra);
    } catch {
      if (win) win.close();
    } finally { setBusyLabel(null); }
  };

  const cancelLabel = async (lbl: LabelRow) => {
    if (busyLabel != null) return;
    if (!window.confirm(`Cancel parcel ${lbl.tracking_number}?\n\nThe label stays on the order marked cancelled (you can still comment). `
      + `The carrier is voided automatically only where supported — otherwise you'll be reminded to void it in the carrier portal.`)) return;
    setBusyLabel(lbl.id);
    try {
      const res = await authFetch(`${API}/labels/${lbl.id}/cancel`, {
        method: 'POST', body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Cancel failed'); return; }
      if (data.warning) alert(`Marked cancelled in the WMS.\n\n⚠ ${data.warning}`);
      await load();
    } catch {
      alert('Cancel failed');
    } finally { setBusyLabel(null); }
  };

  const toggleNotFound = async (itemId: string) => {
    const next = new Set(notFound);
    next.has(itemId) ? next.delete(itemId) : next.add(itemId);
    setNotFound(next);
    await authFetch(`${API}/orders/${orderId}/not-found`, {
      method: 'PATCH',
      body: JSON.stringify({ items: Array.from(next) }),
    });
  };

  const moveToTab = async (tab: string) => {
    if (moving) return;
    setMoving(true);
    await authFetch(`${API}/orders/move`, {
      method: 'POST',
      body: JSON.stringify({ ids: [orderId], tab }),
    });
    setMoving(false);
    if (onMoved) onMoved();
    onClose();
  };

  // Pull this one order straight from the shop. The background sync gets there
  // eventually; this is for the moment right after someone has edited the order
  // in the store and needs the WMS to agree with it before printing anything.
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<
    { changed: boolean; changes: string[]; failed?: boolean; labelPrinted?: boolean;
      exchangeWarning?: string | null } | null>(null);

  const resyncOrder = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await authFetch(`${API}/orders/${orderId}/resync`, { method: 'POST' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSyncResult({ changed: false, failed: true,
                        changes: [body.detail || `Sync failed (HTTP ${res.status})`] });
        return;
      }
      setSyncResult({ changed: !!body.changed, changes: body.changes || [],
                      labelPrinted: !!body.label_printed,
                      exchangeWarning: body.exchange_warning || null });
      await load();
      // NOT onMoved — that closes the panel. The order did not leave its tab,
      // it changed in place, and the result banner is the point of the click.
      if (body.changed && onChanged) onChanged();
    } catch (e: any) {
      setSyncResult({ changed: false, failed: true, changes: [`Sync failed: ${e?.message || e}`] });
    } finally {
      setSyncing(false);
    }
  };

  const printDispatchNote = async () => {
    if (printingNote) return;
    // Open the window synchronously (popup blockers); presentPdf swaps the
    // placeholder for the toolbar + PDF layout, which works in both render-
    // and download-mode browsers (see labelPrint.ts).
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Generating delivery note…</p>');
    setPrintingNote(true);
    try {
      const res = await reprintApproval.run(approver => authFetch(`${API}/orders/dispatch-notes`, {
        method: 'POST',
        body: JSON.stringify({ ids: [orderId], ...approver }),
      }));
      if (!res) { if (win) win.close(); return; }        // the reprint approval was cancelled
      if (!res.ok) {
        if (win) win.close();
        const d = await res.json().catch(() => ({} as any));
        alert(typeof d.detail === 'string' ? d.detail : 'No dispatch note found.');
        return;
      }
      const blob = await res.blob();
      presentPdf(win, blob,
                 { title: 'Delivery note', filename: 'delivery-note.pdf', autoPrint: false },
                 res.headers.get('X-Doc-Token'));
    } finally {
      setPrintingNote(false);
    }
  };

  if (!detail) {
    return (
      <div className="fixed inset-0 z-40 flex">
        <div className="hidden md:block flex-1 bg-black/60" onClick={onClose} />
        <div className="w-full md:w-[80vw] bg-[#0f1117] border-l border-gray-800 flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  const raw = detail.raw || {};
  const ci = raw.customer_information || {};
  const sa = raw.shipping_address || ci;
  const payments = raw.payments || [];
  const products = (raw.products || []).filter((p: any) => (p.quantity ?? 0) > 0);
  // MCF shipments carry type/status; Shopify-normalized ones are bare
  // {external_pickup_point} entries — treat those as active deliveries.
  const shipments = (raw.shipments || []).filter((s: any) =>
    s.type ? (s.type === 'DELIVERY' && s.status !== 'CANCELLED') : true);
  const activeShipment = shipments[shipments.length - 1] || null;
  const pickupPoint = (activeShipment?.external_pickup_point_id?.trim() || activeShipment?.external_pickup_point?.code)
    ? activeShipment.external_pickup_point
    : null;
  const isPickup = !!pickupPoint;
  const deliveryOption = isPickup ? 'Pickup point' : 'Home delivery';
  const ft = detail.tab_first_times || {};
  const payment = payments[0] || null;

  const shippingCost = raw.shipping_costs ?? 0;
  const paymentCosts = raw.payment_costs ?? 0;
  const paymentFees = raw.payment_fees ?? 0;          // WGR's spelling of the same thing
  const productTotal = products.reduce((sum: number, p: any) => sum + (p.unit_price ?? 0) * (p.quantity ?? 0), 0);
  // Line prices are the shop's own, before discount, on all three platforms — so the
  // discount is a row of its own and has to come off the total. It used to be folded
  // into Shopify's unit prices, which is why the panel showed a total nobody could
  // trace back to the webshop.
  const discount = typeof raw.discount === 'object' && raw.discount
    ? Math.abs(Number(raw.discount.value ?? 0))
    : Math.abs(Number(raw.discount ?? 0));
  const discountCode = (typeof raw.discount === 'object' && raw.discount?.code) || '';
  const grandTotal = productTotal + shippingCost + paymentCosts + paymentFees - discount;
  const removedLines = raw.removed_lines || [];

  const differentShipping = raw.different_shipping_address &&
    sa.street_address !== ci.street_address;

  return (
    <div className="fixed inset-0 z-40 flex">
      {reprintApproval.box}
      <div className="hidden md:block flex-1 bg-black/60" onClick={onClose} />

      <div className="w-full md:w-[80vw] bg-[#0f1117] md:border-l border-gray-800 flex flex-col overflow-hidden">

        {lookOnly && (
          <div className="flex-none bg-blue-950/60 border-b border-blue-900 px-3 md:px-6 py-2 text-sm text-blue-200">
            Looking only — changes to an order are made by a manager.
          </div>
        )}
        {/* Header */}
        <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-3 md:px-6 py-3 md:py-4 flex flex-wrap gap-2 items-center justify-between">
          {/* The customer-facing number (SK4218), not the WMS id (183741) —
              it is what the delivery note, the label, the shop and customer
              service all quote. Falls back to the id while the detail loads
              or for a platform that has no separate number. */}
          <div>
            <h2 className="text-white font-bold text-xl">
              <OrderNo value={detail?.order_number || orderId} prefix="Order " />
            </h2>
            <div className="text-gray-400 text-sm">{detail.store_name || detail.webshop_id || ''}
              {detail.storefront?.domain ? <span className="text-gray-500"> · {detail.storefront.domain}</span> : null}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <span className={`text-xs px-2.5 py-1 rounded-full ${
              detail.tab === 'archived' ? 'bg-green-900/60 text-green-400' :
              detail.tab === 'printed' ? 'bg-blue-900/60 text-blue-400' :
              'bg-gray-700 text-gray-400'
            }`}>
              {tabLabel(detail.tab)}
            </span>
            {canProcess && detail.tab !== 'archived' && detail.tab !== 'deleted' && (
              <button onClick={processThis} disabled={processingOne}
                title="Mark this order processed in the store (fulfilled + tracking to the customer) and archive it"
                className="text-white text-sm px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 rounded-lg transition-colors">
                {processingOne ? 'Processing…' : 'Process order'}
              </button>
            )}
            <button
              onClick={resyncOrder}
              disabled={syncing}
              title="Fetch this order again from the shop — use it right after the order was changed there"
              className="text-white text-sm px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-lg transition-colors"
            >
              {syncing ? 'Syncing…' : '⟳ Sync from store'}
            </button>
            <button onClick={onClose}
              className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">
              ✕ Close
            </button>
          </div>
        </div>

        {/* Label issue, label now bought: say what to do, right here — until the order is processed (Fernando 9.10) */}
        {detail.label_ready && (
          <div className="flex-none px-6 py-3 border-b bg-green-950/40 border-green-800/60 text-green-200 text-sm flex items-center gap-3 flex-wrap">
            <span className="flex-1 min-w-[16rem]">
              <span className="font-semibold">🏷 Label ready</span> ({detail.label_ready.carrier} {detail.label_ready.tracking}).
              {detail.label_ready.printed
                ? <>{' '}Sent to the printer {fmtWh(detail.label_ready.printed_at || null, 'short')}. If it is on the parcel, Process; if not, print it again.</>
                : <>{' '}The parcel is already packed: print the label, stick it on, then Process.</>}
            </span>
            <button onClick={async () => { await reprintLabel(detail.label_ready!.label_id); load(); onChanged?.(); }}
              disabled={busyLabel != null}
              className="px-3 py-1.5 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white text-xs font-semibold rounded-lg">
              {busyLabel === detail.label_ready.label_id ? '…' : (detail.label_ready.printed ? '🖨 Print again' : '🖨 Print label')}
            </button>
            {canProcess && (
              <button onClick={processThis} disabled={processingOne}
                className="px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white text-xs font-semibold rounded-lg">
                {processingOne ? 'Processing…' : 'Process order'}
              </button>
            )}
          </div>
        )}

        {/* The label failed and there is still none: say so, and let a person ask the Doctor again */}
        {detail.label_doctor && !detail.label_ready && (
          <div className="flex-none px-6 py-2.5 border-b bg-amber-950/30 border-amber-900/50 text-amber-200 text-sm flex items-center gap-3 flex-wrap">
            <span className="flex-1 min-w-[16rem]">
              <span className="font-semibold">Label failed:</span> {detail.label_doctor.error.slice(0, 220)}
              <span className="text-amber-300/70"> — the Label Doctor looks at it daily until it is fixed.</span>
            </span>
            {/* its latest verdict, so a finished re-look is visible here and not only in the comments */}
            {detail.label_doctor.last && !detail.label_doctor.working && (
              <span className="basis-full order-last text-xs text-amber-100/90 bg-amber-950/40 border border-amber-800/40 rounded-lg px-3 py-2">
                <span className="font-semibold">🩺 Label Doctor ({fmtWh(detail.label_doctor.last.at, 'short')}):{' '}
                  {detail.label_doctor.last.applied ? 'fixed it and asked for a new label'
                    : detail.label_doctor.last.outcome === 'needs_human' ? 'needs a person'
                    : detail.label_doctor.last.outcome === 'island_no_ship' ? "can't ship there"
                    : detail.label_doctor.last.outcome}</span>
                {' — '}{detail.label_doctor.last.note}
              </span>
            )}
            {detail.label_doctor.working ? (
              <span className="basis-full text-xs text-sky-200 bg-sky-950/40 border border-sky-800/50 rounded-lg px-3 py-2">
                <span className="font-semibold">🩺 The Label Doctor is working on this order</span>
                {' '}(started {fmtWh(detail.label_doctor.working.started_at, 'time')}, try {detail.label_doctor.working.try} of {detail.label_doctor.working.of})
                {(detail.label_doctor.working.steps || []).slice(-4).map((st, i) => (
                  <span key={i} className="block text-sky-300/80 truncate">· {st.text}</span>
                ))}
              </span>
            ) : detail.label_doctor.relook_requested_at ? (
              <span className="text-xs text-amber-300">🩺 Asked again {fmtWh(detail.label_doctor.relook_requested_at)}{detail.label_doctor.relook_requested_by ? ` by ${detail.label_doctor.relook_requested_by}` : ''} — the Doctor takes it within 5 minutes</span>
            ) : canProcess && (
              <button onClick={askDoctor} disabled={askingDoctor}
                className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white text-xs font-semibold rounded-lg">
                {askingDoctor ? '…' : '🩺 Ask the Label Doctor again'}
              </button>
            )}
          </div>
        )}
        {/* customer mails: asking the customer / the store manager (docs/CUSTOMER-MAILS.md) */}
        {(detail.tab === 'label_issue' || detail.label_doctor) && <CustomerCaseBox orderId={orderId} />}

        {/* What the last re-sync found */}
        {syncResult && (
          <div className={`flex-none px-6 py-3 border-b text-sm flex items-start gap-3 ${
            syncResult.failed  ? 'bg-red-950/30 border-red-900/50 text-red-300' :
            syncResult.changed ? 'bg-amber-950/30 border-amber-900/50 text-amber-200'
                               : 'bg-gray-900/60 border-gray-800 text-gray-400'
          }`}>
            <div className="flex-1">
              {syncResult.failed ? (
                <span className="font-semibold">{syncResult.changes[0]}</span>
              ) : syncResult.changed ? (
                <>
                  <span className="font-semibold">The order changed in the store:</span>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    {syncResult.changes.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                  {syncResult.labelPrinted && (
                    <div className="mt-2 text-amber-400">
                      A shipping label was already printed for this order — check that the
                      parcel still matches before it goes out.
                    </div>
                  )}
                </>
              ) : (
                <span>Up to date — the store has no changes for this order.</span>
              )}
              {syncResult.exchangeWarning && (
                <div className="mt-2 text-purple-300">⇄ {syncResult.exchangeWarning}</div>
              )}
            </div>
            <button onClick={() => setSyncResult(null)}
              className="text-xs opacity-60 hover:opacity-100 flex-none">✕</button>
          </div>
        )}

        {/* Exchange pass in progress: raw/products below stay the ORIGINAL order —
            the warehouse is picking only the replacement, and this banner is what
            keeps those two facts from looking like a contradiction. */}
        {detail.exchange_pass && (
          <div className="flex-none px-6 py-3 border-b border-purple-900/50 bg-purple-950/30 text-sm text-purple-200 flex items-start gap-3">
            <div className="flex-1">
              <span className="font-semibold">
                Exchange in progress (E{detail.exchange_pass.id})
              </span>
              {' — sending '}
              <span className="text-white">
                {(detail.exchange_pass.sending || []).map((i: any) =>
                  `${titleEn(i) || i.article_number || '?'}${(i.qty || 1) > 1 ? ` ×${i.qty}` : ''}`).join(', ')}
              </span>
              {' in place of '}
              <span className="text-white">
                {(detail.exchange_pass.replaces || []).map((i: any) =>
                  `${i.display || titleEn(i) || i.article_number || '?'}${(i.qty || 1) > 1 ? ` ×${i.qty}` : ''}`).join(', ')}
              </span>
              <div className="mt-1 text-purple-300/80 text-xs">
                The order below is the original as the customer placed it. The pick list,
                packing scan and delivery note carry only the replacement.
              </div>
            </div>
          </div>
        )}

        {/* A resend: this row re-sends the goods of another order after the
            carrier lost the parcel. It has no shop order behind it. */}
        {detail.kind === 'resend' && detail.resend_of && (
          <div className="flex-none px-6 py-3 border-b border-amber-900/50 bg-amber-950/30 text-sm text-amber-200 flex items-start gap-3">
            <div className="flex-1">
              <span className="font-semibold">↻ Resend of </span>
              <button onClick={() => onOpenOrder?.(detail.resend_of!.order_id)}
                className="text-white underline decoration-dotted hover:text-amber-100">
                {detail.resend_of.order_number}
              </button>
              {detail.resend?.reason && <> — {detail.resend.reason}</>}
              {detail.resend?.note && <span className="text-amber-300/80"> — {detail.resend.note}</span>}
              <div className="mt-1 text-amber-300/80 text-xs">
                Created {detail.resend?.created_at ? fmtWh(detail.resend.created_at, 'short') : ''} by {detail.resend?.created_by || '?'}
                {' · '}{detail.resend?.status === 'dispatched'
                  ? `dispatched${detail.resend?.tracking_number ? ` — ${detail.resend.tracking_carrier || ''} ${detail.resend.tracking_number}` : ''}`
                  : detail.resend?.status === 'cancelled' ? `cancelled${detail.resend?.cancel_note ? ` — ${detail.resend.cancel_note}` : ''}`
                  : detail.resend?.reserved ? 'stock reserved in the store’s system' : 'NOT every item could be reserved — see Review'}
                {'. '}No shop order behind this row: Process archives it here and notes the tracking on the original.
                {' '}The Resent products table below shows what is waited back and what is sent.
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex flex-col md:flex-row flex-1 overflow-y-auto md:overflow-hidden">

          {/* Left — main content */}
          <div className="md:flex-1 md:overflow-y-auto p-3 md:p-5 space-y-4">

            {/* Flagged orders (Fernando, 2026-09-29): why the rules pulled this order out of
                picking, with the evidence; CS releases, a manager can trust the customer. */}
            {detail.flag_reason && (detail.flag_reason.rules || []).length > 0 && (
              <div className={`rounded-xl border px-4 py-3 ${detail.tab === 'flagged' ? 'border-red-800/70 bg-red-950/30' : 'border-gray-800 bg-[#1a1d27]'}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${detail.tab === 'flagged' ? 'text-red-200' : 'text-gray-300'}`}>
                      ⚑ {detail.flag_reason.auto_deleted
                          ? (detail.flag_reason.auto_deleted.by === 'Claude' ? 'Deleted automatically — it was flagged' : `Deleted by ${detail.flag_reason.auto_deleted.by} — it was flagged`)
                          : detail.tab === 'flagged' ? 'Flagged' : detail.flag_reason.released_by ? `Was flagged — released by ${detail.flag_reason.released_by}` : 'Flag rule hit (not moved)'}
                      {detail.flag_reason.at ? <span className="text-xs text-gray-500 font-normal"> · {fmtWh(detail.flag_reason.at + 'Z')}</span> : null}
                    </div>
                    {/* the automatic delete (2026-10-12): what the shop did, or why it has not happened */}
                    {detail.flag_reason.auto_deleted && (
                      <div className="mt-1 text-xs text-gray-400">{detail.flag_reason.auto_deleted.shop}{detail.flag_reason.auto_deleted.at ? ` · deleted ${fmtWh(detail.flag_reason.auto_deleted.at + 'Z')}` : ''}</div>
                    )}
                    {detail.tab === 'flagged' && detail.flag_reason.auto_delete && (detail.flag_reason.auto_delete.attempts || detail.flag_reason.auto_delete.capped_at || detail.flag_reason.auto_delete.shop_cancelled_at) && (
                      <div className="mt-1 text-xs text-amber-300">
                        Automatic delete: {detail.flag_reason.auto_delete.shop_cancelled_at ? 'cancelled in the shop — the delete here is pending; do NOT release or ship it'
                          : detail.flag_reason.auto_delete.gave_up_at ? `failed ${detail.flag_reason.auto_delete.attempts} times — a person decides`
                          : detail.flag_reason.auto_delete.attempts ? `attempt ${detail.flag_reason.auto_delete.attempts} failed, tried again later`
                          : 'waits — the daily limit of automatic deletes is reached'}
                        {detail.flag_reason.auto_delete.last_error ? ` (${detail.flag_reason.auto_delete.last_error})` : ''}
                      </div>
                    )}
                    <ul className="mt-1 text-sm text-gray-200 space-y-1">
                      {detail.flag_reason.rules.map((r: any, i: number) => (
                        <li key={i}><span className="text-white">{r.text}</span>{r.evidence?.length ? <span className="text-gray-400"> — {r.evidence.join('; ')}</span> : null}</li>
                      ))}
                    </ul>
                    {(detail.flag_reason.notes || []).length > 0 && (
                      <div className="mt-1 text-xs text-gray-500">{detail.flag_reason.notes.join(' · ')}</div>
                    )}
                    {detail.flag_reason.release_note && <div className="mt-1 text-xs text-gray-500">Release note: {detail.flag_reason.release_note}</div>}
                  </div>
                  {detail.tab === 'flagged' && (
                    <div className="flex gap-2 flex-none">
                      {/* cancelled in the shop already: it must never ship — no Release / Trust (review 12.10) */}
                      {!detail.flag_reason.auto_delete?.shop_cancelled_at && <button onClick={async () => {
                          const note = window.prompt('Release to New — note (what CS agreed with the customer):', '');
                          if (note === null) return;
                          const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/flag/release`, { method: 'POST', body: JSON.stringify({ note }) });
                          const d = await r.json().catch(() => ({}));
                          if (!r.ok) { alert(d.detail || `HTTP ${r.status}`); return; }
                          onMoved ? onMoved() : onClose();
                        }}
                        className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-sm">Release to New</button>}
                      {canResend && !detail.flag_reason.auto_delete?.shop_cancelled_at && (
                        <button onClick={async () => {
                            const note = window.prompt('Trust this customer — every order of theirs in Flagged goes back to New and the rules skip them from now on. Why?', '');
                            if (note === null) return;
                            if (!note.trim()) { alert('A note is required.'); return; }
                            const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/flag/trust`, { method: 'POST', body: JSON.stringify({ note }) });
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok) { alert(d.detail || `HTTP ${r.status}`); return; }
                            onMoved ? onMoved() : onClose();
                          }}
                          title="Manager/admin: the customer is fine (e.g. the carrier lost the parcel) — the rules skip their e-mail/phone"
                          className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm">Trust customer</button>
                      )}
                      {canResend && detail.flag_delete_possible && (
                        <button onClick={async () => {
                            if (!window.confirm(`Delete ${detail.order_number || 'this order'}? It is cancelled in the shop (the shop returns the stock) and deleted here, with a note saying it was flagged.`)) return;
                            const r = await authFetch(`${API}/orders/${encodeURIComponent(orderId)}/flag/delete`, { method: 'POST' });
                            const d = await r.json().catch(() => ({}));
                            if (!r.ok) { alert(d.detail || `HTTP ${r.status}`); return; }
                            onMoved ? onMoved() : onClose();
                          }}
                          title="Manager/admin: cancel this cash-on-delivery order in the shop and delete it — what the automatic delete does"
                          className="px-3 py-1.5 rounded bg-red-800 hover:bg-red-700 text-white text-sm">Delete now</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ordered by */}
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
              <SectionHead title="Ordered by" />
              <Row label="Customer"
                value={[ci.first_name, ci.last_name].filter(Boolean).join(' ') || raw.customer_name} />
              <Row label="Address"
                value={[ci.street_address, ci.postal_code, ci.city, ci.country].filter(Boolean).join(', ')} />
              <Row label="Phone" value={ci.phone} />
              <Row label="Email" value={ci.email} />
              {/* Contact repair: what was corrected, borrowed or marked wrong, and the
                  customer-support mark that feeds it (docs/CONTACT-REPAIR.md) */}
              {(detail.contact_corrections || []).map(c => (
                <div key={c.id} className="flex border-b border-gray-800 text-xs">
                  <div className="w-44 px-4 py-2 text-amber-500 flex-none">
                    {CONTACT_FIELDS.find(f => f.key === c.field)?.label || c.field} {c.corrected_to ? 'corrected' : 'wrong'}
                  </div>
                  <div className="flex-1 px-4 py-2 text-gray-300">
                    {c.customer_gave ? <span className="line-through text-gray-500 mr-2">{c.customer_gave}</span> : null}
                    {c.corrected_to ? <span className="text-white">{c.corrected_to}</span> : <span className="text-gray-500">no correct value known</span>}
                    <span className="text-gray-500"> · {sourceText(c)} · {c.username}{c.created_at ? ` · ${fmt(c.created_at)}` : ''}</span>
                    {c.note && c.source === 'cs' && <div className="text-gray-400 mt-0.5">{c.note}</div>}
                  </div>
                </div>
              ))}
              <div className="px-4 py-2">
                {!markWrong ? (
                  <button onClick={() => setMarkWrong({ field: 'phone', value: '', note: '' })}
                    className="text-xs text-gray-400 hover:text-white">Customer gave wrong details…</button>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2">
                      <select value={markWrong.field} onChange={e => setMarkWrong({ ...markWrong, field: e.target.value })}
                        className="bg-gray-800 border border-gray-700 text-white text-xs rounded-lg px-2 py-1.5">
                        {CONTACT_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      <input value={markWrong.value} onChange={e => setMarkWrong({ ...markWrong, value: e.target.value })}
                        placeholder="Correct value (leave empty if unknown)"
                        className="flex-1 bg-gray-800 border border-gray-700 text-white text-xs rounded-lg px-2 py-1.5" />
                    </div>
                    <input value={markWrong.note} onChange={e => setMarkWrong({ ...markWrong, note: e.target.value })}
                      placeholder="Note, e.g. customer emailed: number was mistyped"
                      className="w-full bg-gray-800 border border-gray-700 text-white text-xs rounded-lg px-2 py-1.5" />
                    <div className="flex gap-2">
                      <button onClick={saveMarkWrong} disabled={savingMark}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded-lg">
                        {savingMark ? 'Saving…' : 'Record'}
                      </button>
                      <button onClick={() => setMarkWrong(null)} className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-lg">Cancel</button>
                    </div>
                    <p className="text-gray-500 text-xs">
                      The wrong value is never reused — not for this label, not for the customer's later orders. A correct value, if given, is used for the label.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery */}
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
              <SectionHead title="Delivery" />
              <Row label="Delivery option" value={deliveryOption} />
              <Row label="Delivery method" value={
                <span className="inline-flex items-center gap-2">
                  {detail.carrier}
                  {detail.payment_kind === 'cod' && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-600 text-white"
                      title={`Cash on delivery (${detail.payment_method || ''}) — the carrier collects ${money(detail.amount)}`}>COD</span>
                  )}
                </span>
              } />
              {isPickup && pickupPoint && (
                <Row label="Pickup point" value={
                  <div>
                    <div className="font-medium">{pickupPoint.name}</div>
                    <div className="text-gray-400 text-xs mt-0.5">
                      {[pickupPoint.street_address, pickupPoint.postal_code, pickupPoint.city, pickupPoint.country]
                        .filter(Boolean).join(', ')}
                    </div>
                  </div>
                } />
              )}
              {!isPickup && differentShipping && (
                <Row label="Ship to"
                  value={[sa.street_address, sa.postal_code, sa.city, sa.country].filter(Boolean).join(', ')} />
              )}
            </div>

            {/* Other info */}
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
              <SectionHead title="Other info" />
              {activeShipment?.weight != null && (
                <Row label="Total weight" value={`${Math.round(activeShipment.weight * 1000)} gram`} />
              )}
              <Row label="Storefront" value={detail.storefront ? <span title={detail.storefront.source}>{detail.storefront.label}</span> : undefined} />
              <Row label="Ordered via" value={raw.source} />
              <Row label="Ordered" value={fmt(detail.order_date) || fmt(raw.created_at || raw.orderTime)} />
              {ft.printed && <Row label="Printed time" value={fmt(ft.printed)} />}
              {ft.hard_to_find && <Row label="Hard to find" value={fmt(ft.hard_to_find)} />}
              {ft.cant_find && <Row label="Can't find product" value={fmt(ft.cant_find)} />}
              {ft.label_issue && <Row label="Label issue" value={fmt(ft.label_issue)} />}
              {ft.pending && <Row label="Pending orders" value={fmt(ft.pending)} />}
              {ft.archived && <Row label="Processed/Sent" value={fmt(ft.archived)} />}
              {payment && (
                <Row label="Payment" value={
                  <div>
                    <span className="font-medium">
                      {payment.payment_option_display_name || payment.payment_option}
                    </span>
                    <div className="text-gray-400 text-xs mt-0.5 space-x-2">
                      <span>Status: {payment.status}</span>
                      <span>·</span>
                      <span>Ref: {payment.reference}</span>
                      <span>·</span>
                      <span>{fmt(payment.created_at)}</span>
                    </div>
                  </div>
                } />
              )}
              {/* Show all WMS-printed labels (newest first); fall back to MCF tracking if none */}
              {(detail.labels?.length ?? 0) > 0
                ? (detail.labels as LabelRow[]).map((lbl, i) => (
                    <Row key={lbl.id ?? i}
                      label={i === 0 ? "Tracking" : ""}
                      value={
                        <div>
                          <span className={`font-mono ${lbl.cancelled_at ? 'text-gray-500 line-through' : 'text-blue-400'}`}>
                            {lbl.tracking_number}
                            {lbl.carrier && <span className="text-gray-600 text-xs ml-2 no-underline">({lbl.carrier.replace('_',' ')})</span>}
                          </span>
                          {lbl.cancelled_at && (
                            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-900/50 text-red-400 align-middle">
                              Cancelled{lbl.cancelled_by ? ` · ${lbl.cancelled_by}` : ''}
                            </span>
                          )}
                          {/* Spent by dispatch: on an archived order, a live label bought
                              before the dispatch is the one the parcel left with. Reprint
                              still works on it; Order label / the packing scan buy anew. */}
                          {!lbl.cancelled_at && detail.tab === 'archived' && detail.dispatched_at
                            && lbl.printed_at && Date.parse(lbl.printed_at) < Date.parse(detail.dispatched_at) && (
                            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 align-middle"
                                  title={`Left with the parcel ${fmtWh(detail.dispatched_at, 'short')}`}>
                              🚚 shipped
                            </span>
                          )}
                          <span className="text-gray-500 text-xs ml-2">
                            {fmt(lbl.printed_at)}{lbl.printed_by ? ` · ${lbl.printed_by}` : ''}
                          </span>
                          {lbl.tracking_alt && (
                            <div className="text-gray-400 text-xs mt-0.5">
                              also: <span className="font-mono">{lbl.tracking_alt}</span>
                              <span className="text-gray-600">
                                {' '}({lbl.carrier === 'packeta' ? 'Packeta code'
                                  : lbl.carrier === 'bring' ? 'consignment' : 'second code'})
                              </span>
                            </div>
                          )}
                          {lbl.cancel_note && (
                            <div className="text-red-400/80 text-xs mt-0.5">✕ {lbl.cancel_note}</div>
                          )}
                          {lbl.pickup_point_name && (
                            <div className="text-gray-400 text-xs mt-0.5">
                              → {lbl.pickup_point_name}
                              {lbl.pickup_point_id && <span className="text-gray-600"> ({lbl.pickup_point_id})</span>}
                            </div>
                          )}
                          {lbl.matched_by === 'b2c_fallback' && (
                            <div className="text-amber-400 text-xs mt-0.5 font-medium">
                              ⚠ Sent as HOME DELIVERY to the pickup point address — locker could not be identified
                            </div>
                          )}
                          {editingLabel === lbl.id ? (
                            <div className="flex gap-2 mt-1 items-center">
                              <input
                                autoFocus
                                value={labelComment}
                                onChange={e => setLabelComment(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveLabelComment();
                                  if (e.key === 'Escape') { setEditingLabel(null); setLabelComment(''); }
                                }}
                                placeholder="e.g. mistake print, wrong pickup location, right location…"
                                className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white placeholder-gray-600"
                              />
                              <button onClick={saveLabelComment}
                                className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white">Save</button>
                              <button onClick={() => { setEditingLabel(null); setLabelComment(''); }}
                                className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300">✕</button>
                            </div>
                          ) : lbl.comment ? (
                            <div
                              className="text-yellow-500/90 text-xs mt-0.5 cursor-pointer hover:text-yellow-400"
                              title="Click to edit comment"
                              onClick={() => { setEditingLabel(lbl.id); setLabelComment(lbl.comment || ''); }}>
                              💬 {lbl.comment}
                              {lbl.comment_by && <span className="text-gray-600"> — {lbl.comment_by}</span>}
                            </div>
                          ) : (
                            <button
                              className="block text-gray-600 text-xs mt-0.5 hover:text-gray-400"
                              onClick={() => { setEditingLabel(lbl.id); setLabelComment(''); }}>
                              + add comment
                            </button>
                          )}
                          {/* The output ledger: bought (printed_at above) is not printed. A cancelled
                              label never prints — it must not read like a live one waiting for the scan. */}
                          <div className={`text-xs mt-0.5 ${lbl.cancelled_at ? 'text-gray-600' : lbl.sent_to_printer_at ? 'text-gray-500' : 'text-amber-500/80'}`}>
                            {lbl.sent_to_printer_at
                              ? <>🖨 printed {fmtWh(lbl.sent_to_printer_at, 'short')}
                                  {lbl.sent_to_printer_by ? ` by ${lbl.sent_to_printer_by}` : ''}
                                  {(lbl.print_count ?? 0) > 1 ? ` ×${lbl.print_count}` : ''}
                                  {lbl.cancelled_at ? ' — then cancelled' : ''}</>
                              : lbl.cancelled_at
                              ? 'cancelled — never printed'
                              : 'not printed yet — prints when the delivery note is scanned for packing'}
                          </div>
                          {!lbl.cancelled_at && (
                            <div className="flex gap-2 mt-1.5">
                              {lbl.can_reprint && (
                                <button
                                  disabled={busyLabel === lbl.id}
                                  onClick={() => reprintLabel(lbl.id)}
                                  title="Print this exact label again (no new shipment)"
                                  className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200">
                                  {busyLabel === lbl.id ? '…' : '🖨 Reprint'}
                                </button>
                              )}
                              <button
                                disabled={busyLabel === lbl.id}
                                onClick={() => cancelLabel(lbl)}
                                title="Cancel/void this shipping label at the carrier"
                                className="text-xs px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/60 disabled:opacity-50 text-red-400">
                                ✕ Cancel label
                              </button>
                            </div>
                          )}
                        </div>
                      } />
                  ))
                : activeShipment?.tracking_code
                  ? <Row label="Tracking" value={<span className="font-mono text-blue-400">{activeShipment.tracking_code}</span>} />
                  : null
              }
              {(handovers.length > 0 || (detail.tab === 'archived' && (detail.labels || []).some(l => !l.cancelled_at && l.carrier === 'packeta'))) && (
                <Row label="Handover" value={handovers.length ? (
                  <div className="space-y-0.5">
                    {handovers.map((h, i) => (
                      <div key={i}>
                        <a href={h.kind === 'packeta' ? '/packeta' : '/bring-hrx'} className="font-mono text-blue-400 hover:underline">{h.code}</a>
                        {h.ship_date && <span className="text-gray-400 text-xs ml-2">{new Date(`${h.ship_date}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'numeric' })}</span>}
                        <span className="text-gray-500 text-xs ml-2">
                          {h.unscanned ? 'went without a scan' : `scanned ${h.at ? fmtWh(h.at.endsWith('Z') ? h.at : `${h.at}Z`, 'short') : ''}${h.by_person ? ` by ${h.by_person}` : ''}`}
                          {h.status === 'open' ? ' · not handed over yet' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : <span className="text-amber-400/80 text-xs">processed — not in a Packeta handover yet</span>} />
              )}
              <Row label="Language / Currency"
                value={[raw.language?.toUpperCase(), payment?.currency].filter(Boolean).join(' / ')} />
            </div>

            {/* Ordered products */}
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
              <SectionHead title="Ordered products" />
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-4 py-2.5 text-left">Article #</th>
                    <th className="px-4 py-2.5 text-left">Location</th>
                    <th className="px-4 py-2.5 text-left">Product</th>
                    <th className="px-4 py-2.5 text-right">Ex. VAT</th>
                    <th className="px-4 py-2.5 text-right">Inc. VAT</th>
                    <th className="px-4 py-2.5 text-center">Qty</th>
                    <th className="px-4 py-2.5 text-right">Sum</th>
                    <th className="px-4 py-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p: any, i: number) => {
                    const incVat = p.unit_price ?? 0;
                    const exVat = p.vat_rate ? incVat / (1 + p.vat_rate / 100) : incVat;
                    const qty = p.quantity ?? 0;
                    // Never let a missing id become the identity: WGR products
                    // carried no `id`, so every row keyed on "undefined" and
                    // marking one item not-found marked the whole order. The
                    // backend now sends WGR's rowId; this guard means any future
                    // platform without one degrades to per-row instead of shared.
                    const itemId = (p.id !== undefined && p.id !== null && p.id !== '')
                      ? String(p.id) : `row-${i}`;
                    const isNF = notFound.has(itemId);
                    // Ordered vs shipped: what packing booked on this line, when it differs
                    const sh = p.line_id ? detail?.shipped_items?.[p.line_id] : undefined;
                    const wrong = sh?.differs ? sh.packed.filter(e => e.resolved && e.variation_id && e.variation_id !== sh.ordered_variation_id) : [];
                    // Split shipment: which parcel(s) this line travels in, and where that parcel stands
                    const lidKey = p.line_id || String(p.variation_id || p.product_code || '');
                    const inParcels = (detail?.split?.parcels || []).filter(pc => lidKey && (pc.lines || {})[lidKey] > 0);
                    return (
                      <tr key={i} className={`border-b border-gray-800 last:border-0 ${isNF ? 'bg-red-950/30' : ''}`}>
                        <td className={`px-4 py-2.5 font-mono text-xs ${isNF ? 'text-gray-600' : 'text-gray-400'}`}
                            title={p.article_number && p.article_number !== p.product_code ? `The shop sent "${p.product_code}" with the order; the variant's article is ${p.article_number} today` : undefined}>
                          {/* the variant's CURRENT article; the order's snapshot when it differs */}
                          {p.article_number || p.product_code}
                          {p.article_number && p.product_code && p.article_number !== p.product_code && (
                            <div className="text-[10px] text-gray-600 line-through">{p.product_code}</div>
                          )}
                        </td>
                        <td className={`px-4 py-2.5 font-mono text-xs ${isNF ? 'text-gray-600' : 'text-blue-400'}`}>{p.location || '—'}</td>
                        <td className={`px-4 py-2.5 ${isNF ? 'text-gray-500' : 'text-white'}`} title={customerWords(p)}>
                          {titleEn(p)}
                          {p.model_name && (
                            <span className="text-gray-400 text-xs ml-1.5">{p.model_name}</span>
                          )}
                          {wrong.map((e, n) => (
                            <span key={n} className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200 align-middle"
                                  title={`Packing scanned barcode ${e.barcode || '?'}${e.at ? ` on ${fmtWh(e.at, 'short')}` : ''}${e.by ? ` by ${e.by}` : ''} — ${sh!.source === 'scan_events' ? 'read back from the pack scans' : 'recorded at packing'}`}>
                              🚚 shipped {e.article_number || e.variation_id}{e.qty > 1 ? ` ×${e.qty}` : ''}
                            </span>
                          ))}
                          {/* WGR line flags (2026-09-22): waiting for a NOS supplier delivery — the
                              same red NOS pill the shop admin shows — with the supplier-order links;
                              and the shop's non-refundable mark */}
                          {p.waiting_for_nos && (
                            <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-700 text-white"
                                    title="Waiting for a NOS (never-out-of-stock) supplier delivery — the shop sold it from supplier stock; it cannot be picked until it arrives">NOS</span>
                              {(p.supplier_order_links || []).map((u: string, k: number) => (
                                <a key={k} href={u} target="_blank" rel="noreferrer" className="text-[10px] text-sky-400 hover:underline" title={u}>supplier order{(p.supplier_order_links || []).length > 1 ? ` ${k + 1}` : ''} ↗</a>
                              ))}
                            </span>
                          )}
                          {p.not_refundable && (
                            <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200 align-middle"
                                  title="Marked non-refundable in the shop (WGR isNotRefundable)">non-refundable</span>
                          )}
                          {inParcels.map(pc => (
                            <span key={pc.id} className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded align-middle ${
                                pc.status === 'dispatched' ? 'bg-green-900/60 text-green-200'
                                : pc.status === 'cancelled' ? 'bg-gray-700 text-gray-400 line-through'
                                : pc.status === 'active' ? 'bg-sky-800 text-sky-100' : 'bg-amber-900/60 text-amber-200'}`}
                              title={`Split shipment — parcel ${pc.parcel_no}: ${pc.banner || pc.status}`}>
                              📦 parcel {pc.parcel_no}{(pc.lines || {})[lidKey] < qty ? ` ×${(pc.lines || {})[lidKey]}` : ''} · {
                                pc.status === 'dispatched' ? 'sent' : pc.status === 'active' ? 'packing now' : pc.status === 'cancelled' ? 'cancelled' : 'later'}
                            </span>
                          ))}
                        </td>
                        <td className={`px-4 py-2.5 text-right ${isNF ? 'text-gray-600' : 'text-gray-300'}`}>{money(exVat)}</td>
                        <td className={`px-4 py-2.5 text-right ${isNF ? 'text-gray-600' : 'text-gray-300'}`}>{money(incVat)}</td>
                        <td className={`px-4 py-2.5 text-center font-medium ${isNF ? 'text-gray-600' : 'text-white'}`}>
                          {qty}
                          {isNF && detail?.not_found_qty?.[itemId] != null && (
                            <div className="text-red-400 text-[10px] font-semibold whitespace-nowrap">{detail.not_found_qty[itemId]} missing</div>
                          )}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-medium ${isNF ? 'text-gray-600' : 'text-white'}`}>
                          {money((incVat * qty))}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button
                            onClick={() => toggleNotFound(itemId)}
                            title={isNF ? 'Mark as found' : 'Mark as not found'}
                            className={`w-7 h-7 rounded border text-sm font-bold transition-colors ${
                              isNF
                                ? 'bg-red-700 border-red-600 text-white'
                                : 'bg-gray-800 border-gray-500 text-gray-300 hover:bg-red-700 hover:border-red-600 hover:text-white'
                            }`}
                          >
                            {isNF ? '✕' : '?'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              {/* Shopify keeps an edited-out line on the order with quantity 0. It is
                  not picked, packed or printed any more, but saying nothing would leave
                  someone comparing the WMS with the webshop and finding one item short
                  with no explanation. */}
              {removedLines.length > 0 && (
                <div className="border-t border-gray-700 px-4 py-2 text-xs text-gray-500">
                  {removedLines.length} line{removedLines.length !== 1 ? 's' : ''} removed in the shop:{' '}
                  {removedLines.map((l: any, i: number) => (
                    <span key={i} className="text-gray-400">
                      {i > 0 && ', '}
                      {lineEn(l)}
                      {l.quantity > 1 ? ` ×${l.quantity}` : ''}
                    </span>
                  ))}
                </div>
              )}
              <div className="border-t border-gray-700 px-4 py-2.5 space-y-1 text-sm">
                {shippingCost > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>Shipping cost</span><span>{money(shippingCost)}</span>
                  </div>
                )}
                {paymentCosts > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>Payment costs</span><span>{money(paymentCosts)}</span>
                  </div>
                )}
                {paymentFees > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>{raw.payment_fee_title || 'Payment fee'}</span><span>{money(paymentFees)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>{detail.kind === 'resend' && detail.resend?.charges && detail.resend.charges.items === false
                      ? 'Not charged (resend)' : `Discount${discountCode ? ` ${discountCode}` : ''}`}</span>
                    <span>-{money(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-white font-semibold">
                  <span>Total inc. VAT</span><span>{money(grandTotal)}</span>
                </div>
              </div>
              {notFound.size > 0 && (
                <div className="px-4 py-3 border-t border-red-900/50 bg-red-950/20 flex items-center gap-3">
                  <span className="text-red-400 text-xs font-semibold flex-1">
                    {notFound.size} item{notFound.size !== 1 ? 's' : ''} not found — move order to:
                  </span>
                  <button
                    onClick={() => moveToTab('hard_to_find')}
                    disabled={moving}
                    className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    Hard to find
                  </button>
                  <button
                    onClick={() => moveToTab('cant_find')}
                    disabled={moving}
                    className="px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    Can't find product
                  </button>
                </div>
              )}
            </div>

            {/* Fernando, 2026-09-18: the original stays original; what came back and what the
                order became are two more tables with the SAME columns (backend
                returns.order_after_returns — the report reads the same numbers). */}
            {detail.returns?.after_return && (() => {
              const ar = detail.returns!.after_return;
              const t = ar.totals || {};
              const head = (extra?: string) => (
                <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-4 py-2.5 text-left">Article #</th>
                  <th className="px-4 py-2.5 text-left">Location</th>
                  <th className="px-4 py-2.5 text-left">Product</th>
                  <th className="px-4 py-2.5 text-right">Ex. VAT</th>
                  <th className="px-4 py-2.5 text-right">Inc. VAT</th>
                  <th className="px-4 py-2.5 text-center">Qty</th>
                  <th className="px-4 py-2.5 text-right">Sum</th>
                  {extra !== undefined && <th className="px-4 py-2.5 text-left">{extra}</th>}
                </tr>
              );
              const exVat = (inc: number, rate: any) => (rate ? inc / (1 + Number(rate) / 100) : inc);
              const cells = (l: any, dim: boolean) => (
                <>
                  <td className={`px-4 py-2.5 font-mono text-xs ${dim ? 'text-gray-600' : 'text-gray-400'}`}>{l.product_code}</td>
                  <td className={`px-4 py-2.5 font-mono text-xs ${dim ? 'text-gray-600' : 'text-blue-400'}`}>{l.location || '—'}</td>
                  <td className={`px-4 py-2.5 ${dim ? 'text-gray-500' : 'text-white'}`}>
                    <span className={dim ? 'line-through' : ''} title={customerWords(l)}>{titleEn(l)}</span>
                    {l.model_name && <span className="text-gray-400 text-xs ml-1.5">{l.model_name}</span>}
                    {l.tag && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-purple-900/70 text-purple-200 no-underline">{l.tag}</span>}
                  </td>
                  <td className={`px-4 py-2.5 text-right ${dim ? 'text-gray-600' : 'text-gray-300'}`}>{money(exVat(l.unit_price, l.vat_rate))}</td>
                  <td className={`px-4 py-2.5 text-right ${dim ? 'text-gray-600' : 'text-gray-300'}`}>{money(l.unit_price)}</td>
                  <td className={`px-4 py-2.5 text-center font-medium ${dim ? 'text-gray-600' : 'text-white'}`}>{l.quantity}</td>
                  <td className={`px-4 py-2.5 text-right font-medium ${dim ? 'text-gray-600' : 'text-white'}`}>{money(l.sum)}</td>
                </>
              );
              const row = (label: string, v: any, cls = 'text-gray-400') => (
                <div className={`flex justify-between ${cls}`}><span>{label}</span><span>{v}</span></div>
              );
              return (
                <>
                  <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
                    <SectionHead title="Returned products" />
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>{head('Returned')}</thead>
                        <tbody>
                          {(ar.returned || []).map((l: any, i: number) => (
                            <tr key={i} className="border-b border-gray-800 last:border-0">
                              {cells(l, false)}
                              <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                                <div className="text-gray-300">{l.accepted_at ? fmtWh(l.accepted_at) : '—'}{l.rcode ? <span className="text-gray-500"> · {l.rcode}</span> : null}</div>
                                <div className="flex gap-1 mt-0.5">
                                  {l.kind === 'shop_return' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-200">shop</span>}
                                  {l.kind !== 'return' && l.kind !== 'shop_return' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-800 text-purple-100">{l.kind === 'unclaimed_cod' ? 'unclaimed COD' : l.kind}</span>}
                                  {l.stage && <span className={`text-[10px] px-1.5 py-0.5 rounded ${STAGE_TONE[l.stage] || 'bg-gray-700 text-gray-200'}`}>{STAGE_LABEL[l.stage] || l.stage}</span>}
                                  {l.note && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{l.note}</span>}
                                  {l.shop_removed && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200">removed in the shop after the return</span>}
                                  {l.subtracted === false && !l.note && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">not subtracted</span>}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="border-t border-gray-700 px-4 py-2.5 space-y-1 text-sm">
                      {row('Returned value (as paid)', money(t.returned_value), 'text-gray-300')}
                      {Object.entries(t.fees_kept || {}).map(([label, amt]: any) => row(`${label} kept`, `-${money(amt)}`))}
                      {(ar.refunds || []).map((r: any) => row(
                        (r.processed
                          ? `Refunded${r.kind === 'cancellation' ? ' (order cancelled)' : ''}${r.processed_by ? ` · ${r.processed_by}` : ''}${r.processed_at ? ` · ${fmtWh(r.processed_at)}` : ''}`
                          : `Refund pending${r.auto?.status ? ` · ${r.auto.status.replace('_', ' ')}` : ''}`)
                        + (r.deducted > 0 ? ` · ${money(r.deducted)} deducted` : ''),
                        money(r.amount), r.processed ? 'text-white font-semibold' : 'text-amber-300'))}
                      {(ar.refunds || []).length === 0 && row('No refund', '—')}
                    </div>
                  </div>

                  <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
                    <SectionHead title="Order after return" />
                    <div className="px-4 pt-2 text-[11px] text-gray-500">
                      As placed in the shop; lines the shop removed later are in the footer.{ar.caption ? ` ${ar.caption}.` : ''}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[640px]">
                        <thead>{head()}</thead>
                        <tbody>
                          {(ar.after || []).map((l: any, i: number) => (
                            <tr key={i} className="border-b border-gray-800 last:border-0">{cells(l, l.quantity === 0)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="border-t border-gray-700 px-4 py-2.5 space-y-1 text-sm">
                      {t.shipping > 0 && row('Shipping cost', money(t.shipping))}
                      {t.payment_costs > 0 && row('Payment costs', money(t.payment_costs))}
                      {t.discount_left > 0 && row('Discount', `-${money(t.discount_left)}`)}
                      {Object.entries(t.fees_kept || {}).map(([label, amt]: any) => row(`${label} kept`, money(amt)))}
                      {t.removed_in_shop_unrefunded > 0 && row('Removed in the shop, refunded outside the WMS?', `-${money(t.removed_in_shop_unrefunded)}`, 'text-amber-300')}
                      {(ar.flags || []).includes('unclaimed') && row('Unclaimed COD — nothing was delivered or collected', '', 'text-amber-300')}
                      {(ar.flags || []).includes('cancelled') && row('Order cancelled — nothing was delivered, the payment was given back', '', 'text-amber-300')}
                      {((ar.flags || []).includes('exchange') || (ar.flags || []).includes('shop_exchange')) && row('Exchange: the replacement is counted at the shop\'s price; a price difference reconciles when the extra payment is on the order', '', 'text-gray-500')}
                      {row('Total after return', money(t.total_after), 'text-white font-semibold')}
                      <div className="border-t border-gray-800 pt-1 mt-1 space-y-1">
                        {((ar.payments || []).length > 1 || ar.payment_state !== 'paid') && (ar.payments || []).map((p: any, i: number) => row(
                          `Payment ${i + 1}${p.option ? ` · ${p.option}` : ''} · ${p.status || '?'}${p.counted ? '' : ' (not counted)'}${p.pending_refund ? ' · refund pending at Paytrail' : ''}`,
                          money(p.amount), p.counted ? 'text-gray-400' : 'text-gray-600'))}
                        {ar.payment_state === 'assumed' && row('No payment record — order total assumed paid', '', 'text-gray-500')}
                        {row('Customer paid', money(t.paid))}
                        {row('Refunded', `-${money(t.refunded)}`)}
                        {t.refund_pending > 0 && row('Refund pending', `-${money(t.refund_pending)}`, 'text-amber-300')}
                        {row('Kept', money(t.kept), 'text-gray-200 font-medium')}
                        {t.difference !== null && t.difference !== undefined && Math.abs(t.difference) >= 0.01 && (
                          <div title="+ we hold more than the order after return is worth (a shop-side refund the WMS did not see?); − we refunded more than the order after return allows">
                            {row('Kept differs from total after return', `${t.difference > 0 ? '+' : ''}${money(t.difference)}`, 'text-red-400 font-semibold')}
                            <div className="text-[11px] text-red-400/80">{t.difference > 0 ? 'We hold more than the order after return is worth — a shop-side refund the WMS did not see?' : 'We refunded more than the order after return allows.'}</div>
                          </div>)}
                        {t.difference !== null && t.difference !== undefined && Math.abs(t.difference) < 0.01 && row('Reconciled — kept equals total after return', '✓', 'text-green-400')}
                        {ar.why_not_reconciled && row(`Not reconciled yet: ${ar.why_not_reconciled}`, '', 'text-amber-300')}
                        {Object.keys(t.over_returned || {}).length > 0 && row('Returned more pieces than ordered', 'check the acceptances', 'text-red-400')}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Every exchange of this order, from the moment it is registered (Fernando,
                2026-09-21): old item(s), replacement(s), the derived timeline with its banner,
                the money — one block per exchange, newest first. Replaces the pass-only card:
                the pass is one row of the timeline now. Data: returns.exchange_history. */}
            {(detail.exchanges || []).length > 0 && (
              <div className="bg-[#1a1d27] rounded-xl border border-purple-900/60 overflow-hidden">
                <div className="bg-purple-950/60 px-4 py-2.5 text-purple-200 text-sm font-semibold">
                  ⇄ Exchanges of this order
                </div>
                <div className="divide-y divide-gray-800">
                  {(detail.exchanges || []).map(ex => {
                    const item = (i: ExchangeItem) => `${titleEn(i) || i.article_number || '?'}${i.qty > 1 ? ` ×${i.qty}` : ''}`;
                    const priceOf = (v: number | null | undefined) => (v === null || v === undefined ? '—' : money(v));
                    return (
                      <div key={ex.id} className="px-4 py-3 text-sm space-y-2.5">
                        {/* header */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">E{ex.id}</span>
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            ex.status === 'processed' ? 'bg-green-900/60 text-green-300'
                            : ex.status === 'open' ? 'bg-purple-800 text-purple-100' : 'bg-gray-700 text-gray-300'}`}>
                            {ex.status}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">
                            {ex.no_return ? 'the customer keeps the original' : ex.send_mode === 'on_return' ? 'send when the return arrives' : 'send now'}
                          </span>
                          {ex.mid_pass && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/70 text-purple-200">pass running</span>}
                          <span className="text-xs text-gray-500">
                            registered {ex.created_at ? fmtWh(ex.created_at, 'short') : ''}{ex.created_by ? ` by ${ex.created_by}` : ''}
                            {ex.reason_text ? ` · ${ex.reason_text}` : ''}
                          </span>
                          {/* Fernando, 2026-09-17: an exchange must be changeable and cancellable from here */}
                          {ex.status === 'open' && (
                            <span className="ml-auto flex gap-1">
                              {!ex.dispatched_at && (
                                <button onClick={() => setEditExchange({
                                    id: ex.id, returned_items: ex.returned, wanted_items: ex.wanted,
                                    reason_text: ex.reason_text || undefined, send_mode: ex.send_mode,
                                    no_return: ex.no_return, dispatched_at: ex.dispatched_at })}
                                  className="text-xs px-2.5 py-1 rounded bg-indigo-700 hover:bg-indigo-600 text-white">
                                  Edit exchange
                                </button>
                              )}
                              <button onClick={() => cancelExchange(ex.id)}
                                className="text-xs px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-gray-300 hover:text-red-300">
                                Cancel exchange
                              </button>
                            </span>
                          )}
                        </div>
                        {/* banner = the current state, from the server */}
                        <div className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-3 ${EX_TONE(ex.status, ex.current)}`}>
                          <span>{ex.banner}</span>
                          {ex.current_at && <span className="text-xs opacity-70 whitespace-nowrap">{fmtWh(ex.current_at, 'short')}</span>}
                        </div>
                        {/* old and new, same columns as the tables above */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[560px]">
                            <thead>
                              <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                                <th className="px-3 py-1.5 text-left"></th>
                                <th className="px-3 py-1.5 text-left">Article #</th>
                                <th className="px-3 py-1.5 text-left">Location</th>
                                <th className="px-3 py-1.5 text-left">Product</th>
                                <th className="px-3 py-1.5 text-center">Qty</th>
                                <th className="px-3 py-1.5 text-right">Paid / price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ex.returned.map((i, n) => (
                                <tr key={`r${n}`} className="border-b border-gray-800/60">
                                  <td className="px-3 py-1.5 text-[10px] uppercase text-gray-500 whitespace-nowrap">{ex.no_return ? 'kept by the customer' : i.wrong_item ? 'comes back' : 'old item'}</td>
                                  <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{i.article_number || '—'}</td>
                                  <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{i.location || '—'}</td>
                                  <td className="px-3 py-1.5 text-gray-200" title={customerWords(i)}>{titleEn(i) || '?'}
                                    {i.wrong_item && (
                                      <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-900/60 text-red-200 align-middle"
                                            title={i.back_source === 'cs' ? 'CS: the customer has this garment, not the line ordered' : 'This garment left at packing instead of the line ordered; it comes back as itself (stock follows the garment). The refund is the sold line\'s.'}>
                                        sold as {i.sold_article || i.sold_name}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-center text-gray-200">{i.qty}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-300">{priceOf(i.paid_unit)}</td>
                                </tr>
                              ))}
                              {ex.wanted.map((i, n) => (
                                <tr key={`w${n}`} className="border-b border-gray-800/60 last:border-0">
                                  <td className="px-3 py-1.5 text-[10px] uppercase text-purple-300 whitespace-nowrap">replacement</td>
                                  <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{i.article_number || '—'}</td>
                                  <td className="px-3 py-1.5 font-mono text-xs text-blue-400">{i.location || '—'}</td>
                                  <td className="px-3 py-1.5 text-white">
                                    {titleEn(i) || '?'}
                                    {ex.status === 'open' && (
                                      <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${i.held ? 'bg-green-900/60 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                                        {i.held ? 'reserved' : 'not reserved'}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-center text-white">{i.qty}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-300" title="as the delivery note prints it (what was paid for the piece it replaces)">
                                    {priceOf(i.unit_price)}
                                    {i.shop_price !== null && i.shop_price !== undefined && <span className="text-gray-600 text-xs"> · shop {money(i.shop_price)}</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* timeline */}
                        <div className="grid gap-x-4 gap-y-0.5 text-xs" style={{ gridTemplateColumns: 'auto auto 1fr' }}>
                          {ex.timeline.map((t, n) => {
                            const isCur = t.state === ex.current;
                            return (
                              <React.Fragment key={n}>
                                <span className={`whitespace-nowrap ${isCur ? 'text-white font-medium' : 'text-gray-500'}`}>
                                  {isCur ? '▸ ' : '· '}{EX_STEP[t.state] || t.state}
                                </span>
                                <span className={`whitespace-nowrap ${isCur ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {t.at ? fmtWh(t.at, 'short') : '—'}{t.by ? ` · ${t.by}` : ''}
                                </span>
                                <span className={isCur ? 'text-gray-400' : 'text-gray-600'}>{t.detail || ''}</span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        {/* money — information only (row 7: "create the exchange as usual") */}
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 border-t border-gray-800 pt-2">
                          <span>paid for the old item{ex.returned.length > 1 ? 's' : ''}: <span className="text-gray-300">{priceOf(ex.money.paid_returned)}</span></span>
                          <span>replacement as the note prints it: <span className="text-gray-300">{priceOf(ex.money.replacement_value)}</span></span>
                          {ex.money.replacement_shop_value !== null && ex.money.replacement_shop_value !== undefined && (
                            <span>at today's shop price: <span className="text-gray-300">{money(ex.money.replacement_shop_value)}</span></span>
                          )}
                          {ex.money.difference !== null && ex.money.difference !== undefined && Math.abs(ex.money.difference) >= 0.01 && (
                            <span title="shop price of the replacement minus what was paid for the old item — shown, not collected or refunded by the WMS">
                              difference: <span className={ex.money.difference > 0 ? 'text-amber-300' : 'text-green-300'}>{ex.money.difference > 0 ? '+' : ''}{money(ex.money.difference)}</span>
                            </span>
                          )}
                          {ex.replacement_label && (
                            <span>label: <span className="font-mono text-gray-300">{ex.replacement_label.tracking_number}</span> ({ex.replacement_label.carrier}){ex.replacement_label.cancelled_at ? ' · cancelled' : ''}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resends of this order — a table per resend: what we wait back, what we send,
                where it stands (Fernando, 2026-09-21: tables, not loose text) */}
            {(detail.resends || []).length > 0 && (
              <div className="bg-[#1a1d27] rounded-xl border border-amber-900/60 overflow-hidden">
                <div className="bg-amber-950/50 px-4 py-2.5 text-amber-200 text-sm font-semibold">
                  ↻ Resent products
                </div>
                <div className="divide-y divide-gray-800">
                  {(detail.resends || []).map(r => (
                    <ResendTable key={r.id} r={r} side="parent" onOpenOrder={onOpenOrder} money={money} tabLabel={tabLabel} />
                  ))}
                </div>
                <div className="border-t border-gray-800 px-4 py-2 text-xs text-gray-500">
                  When the waiting-back pieces arrive, Accept Returns restocks them and raises no refund.
                  {(detail.resends || []).some(r => r.customer_keeps && r.status !== 'cancelled') &&
                    ' Pieces kept by the customer are not expected; if they come back anyway, no refund — held off sale when the reason is Defect or Damaged in transit.'}
                </div>
              </div>
            )}

            {/* The resend order itself: the same table, pointing at the original */}
            {detail.kind === 'resend' && detail.resend && (
              <div className="bg-[#1a1d27] rounded-xl border border-amber-900/60 overflow-hidden">
                <div className="bg-amber-950/50 px-4 py-2.5 text-amber-200 text-sm font-semibold flex items-center gap-2">
                  <span>↻ Resent products</span>
                  {detail.resend_of && (
                    <span className="text-xs font-normal text-amber-300/80">
                      of <button onClick={() => onOpenOrder?.(detail.resend_of!.order_id)} className="underline decoration-dotted hover:text-amber-100">{detail.resend_of.order_number}</button>
                    </span>
                  )}
                </div>
                <ResendTable r={detail.resend} side="resend" onOpenOrder={onOpenOrder} money={money} tabLabel={tabLabel}
                  canCancel={canResend} onCancel={cancelResend} cancelling={cancellingResend} />
              </div>
            )}

            {/* Split shipments (docs/SPLIT-SHIPMENTS.md): one block per parcel — its lines,
                where it stands, what to do next. The Ordered products table above stays the
                order as placed; this is how it leaves the building. */}
            {detail.split && detail.split.parcels.length > 0 && (
              <div className="bg-[#1a1d27] rounded-xl border border-sky-900/60 overflow-hidden">
                <div className="bg-sky-950/50 px-4 py-2.5 text-sky-200 text-sm font-semibold flex items-center gap-3 flex-wrap">
                  <span>📦 Shipments — {detail.split.badge} parcels sent</span>
                  <span className="text-xs font-normal text-sky-300/80 flex-1">
                    {detail.split.between
                      ? 'Between parcels: release the next one to pack it — nothing can be printed or processed until then.'
                      : detail.split.open > 0
                      ? 'The shop is told once, when the last parcel has shipped.'
                      : 'Every parcel has left.'}
                  </span>
                  {canSplit && canCancelSplit && (
                    <button onClick={cancelSplit} disabled={splitBusy}
                      className="text-xs px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-gray-300 hover:text-red-300 font-normal disabled:opacity-40">
                      Cancel split
                    </button>
                  )}
                </div>
                <div className="divide-y divide-gray-800">
                  {detail.split.parcels.map(p => {
                    const tone = p.status === 'dispatched' ? 'border-green-900/60 bg-green-950/40 text-green-200'
                      : p.status === 'cancelled' ? 'border-gray-700 bg-gray-800/60 text-gray-300'
                      : p.status === 'active' ? 'border-sky-900/60 bg-sky-950/40 text-sky-200'
                      : p.ready_at ? 'border-emerald-900/60 bg-emerald-950/40 text-emerald-200'
                      : 'border-amber-900/60 bg-amber-950/40 text-amber-200';
                    return (
                      <div key={p.id} className="px-4 py-3 text-sm space-y-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium">Parcel {p.parcel_no} of {detail.split!.total}</span>
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                            p.status === 'dispatched' ? 'bg-green-900/60 text-green-300'
                            : p.status === 'active' ? 'bg-sky-800 text-sky-100'
                            : p.status === 'cancelled' ? 'bg-gray-700 text-gray-400'
                            : p.ready_at ? 'bg-emerald-800 text-emerald-100' : 'bg-amber-900/60 text-amber-200'}`}
                            title={p.status === 'open' && p.ready_at ? 'The waiting goods arrived (the shop moved the order out of Waiting for NOS) — releases automatically when the active parcel is dispatched' : undefined}>
                            {p.status === 'open' ? (p.ready_at ? 'ready' : 'ships later') : p.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            created {p.created_at ? fmtWh(p.created_at, 'short') : ''}{p.created_by ? ` by ${p.created_by}` : ''}
                            {p.note ? ` · ${p.note}` : ''}
                          </span>
                          {canSplit && (
                            <span className="ml-auto flex gap-1">
                              {p.status === 'open' && !detail.split!.active_parcel_no && (
                                <button onClick={() => releaseParcel(p.parcel_no)} disabled={splitBusy}
                                  className="text-xs px-2.5 py-1 rounded bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-40">
                                  Release parcel {p.parcel_no}
                                </button>
                              )}
                              {(p.status === 'open' || p.status === 'active')
                                && detail.split!.parcels.some(q => q.id !== p.id && q.status !== 'cancelled') && (
                                <button onClick={() => cancelParcel(p.parcel_no, p.status === 'active')} disabled={splitBusy}
                                  title={p.status === 'active' ? 'These lines are not being sent after all — the parcel and its label are cancelled' : 'These lines are not being sent'}
                                  className="text-xs px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-gray-300 hover:text-red-300 disabled:opacity-40">
                                  Cancel parcel
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                        {p.banner && (
                          <div className={`rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-3 ${tone}`}>
                            <span>{p.banner}</span>
                            {p.current_at && <span className="text-xs opacity-70 whitespace-nowrap">{fmtWh(p.current_at, 'short')}</span>}
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[520px]">
                            <thead>
                              <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                                <th className="px-3 py-1.5 text-left">Article #</th>
                                <th className="px-3 py-1.5 text-left">Location</th>
                                <th className="px-3 py-1.5 text-left">Product</th>
                                <th className="px-3 py-1.5 text-center">Qty</th>
                                <th className="px-3 py-1.5 text-right">Inc. VAT</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(p.items || []).map((it, n) => (
                                <tr key={n} className="border-b border-gray-800/60 last:border-0">
                                  <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{it.article_number || '—'}</td>
                                  <td className="px-3 py-1.5 font-mono text-xs text-blue-400">{it.location || '—'}</td>
                                  <td className={`px-3 py-1.5 ${p.status === 'cancelled' ? 'text-gray-500 line-through' : 'text-white'}`} title={customerWords(it)}>{titleEn(it) || '?'}</td>
                                  <td className="px-3 py-1.5 text-center text-white">{it.qty}</td>
                                  <td className="px-3 py-1.5 text-right text-gray-300">{it.unit_price != null ? money(Number(it.unit_price)) : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {(p.timeline || []).length > 0 && (
                          <div className="grid gap-x-4 gap-y-0.5 text-xs" style={{ gridTemplateColumns: 'auto auto 1fr' }}>
                            {(p.timeline || []).map((t, n) => {
                              const isCur = t.state === p.current;
                              return (
                                <React.Fragment key={n}>
                                  <span className={`whitespace-nowrap ${isCur ? 'text-white font-medium' : 'text-gray-500'}`}>{isCur ? '▸ ' : '· '}{PC_STEP[t.state] || t.state}</span>
                                  <span className={`whitespace-nowrap ${isCur ? 'text-gray-300' : 'text-gray-600'}`}>{t.at ? fmtWh(t.at, 'short') : '—'}{t.by ? ` · ${t.by}` : ''}</span>
                                  <span className={isCur ? 'text-gray-400' : 'text-gray-600'}>{t.detail || ''}</span>
                                </React.Fragment>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fernando, 2026-09-18: ONE composer for returns and exchanges. Editing an existing
                exchange keeps the PATCH editor; everything new goes through /settle. */}
            {/* Fernando 27.10: the bottom "Return / exchange" card moved to the right-hand buttons — "Send again"
                (resend or exchange) and "Return…" (an announced return). Editing an exchange stays here. */}
            {editExchange && (
              <ExchangeComposer key={editExchange.id} orderId={orderId} products={products} onCreated={() => { setEditExchange(null); load(); }}
                webshopId={detail.webshop_id} existing={editExchange} onCancelEdit={() => setEditExchange(null)} />
            )}
            {/* labelless Packeta returns: the code the customer says at the counter (2026-09-24) */}
            {detail.packeta_returns && (
              <PacketaReturnCard orderId={orderId} block={detail.packeta_returns}
                onChanged={() => { load(); loadParcels(); }} />
            )}

          </div>

          {/* Right panel */}
          <div className="w-full md:w-72 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col flex-none md:min-h-0">
           {/* everything above the buttons scrolls as ONE column on desktop; on the phone the page
               scrolls (the panel is stacked). The buttons never leave the screen (2026-09-29). */}
           <div className="md:flex-1 md:min-h-0 md:overflow-y-auto">

            {/* Internal comment */}
            <div className="p-4 border-b border-gray-800">
              <div className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2">
                Internal comment
              </div>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={3}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Add a note..."
              />
              <button
                onClick={saveComment}
                disabled={!comment.trim() || saving}
                className="mt-2 w-full py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? 'Saving...' : 'Save the above'}
              </button>
              {detail.comments.length > 0 && (
                /* Capped + scrollable: notes are newest-first, so the latest is
                   always in view and older ones are a scroll away. Uncapped,
                   one long Label Doctor note on a laptop pushed the Handle-
                   order buttons clean off the screen (Fernando, 2026-09-04 —
                   the column is fixed-height, so overflow was just clipped). */
                <div className="mt-3 space-y-2 pr-1">
                  {detail.comments.map(c => (
                    <div key={c.id} className="bg-gray-800 rounded-lg px-3 py-2">
                      {/* break-words: carrier-error notes carry unbroken
                          URL/JSON runs — same fix as the custom-label page */}
                      <div className="text-white text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{c.comment}</div>
                      <div className="text-gray-500 text-xs mt-0.5">
                        {c.username} · {fmt(c.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Parcel tracking — the carrier's own events, newest first */}
            {parcels.length > 0 && (
              <div className="p-4 border-b border-gray-800">
                <div className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2">
                  Parcel tracking
                </div>
                {parcels.map((p: any) => (
                  <div key={p.id} className="mb-3 last:mb-0">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-gray-300">{p.carrier_name || carrierName(p.carrier)}</span>
                      {p.kind === 'return' && (
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-800 text-gray-400"
                              title="the customer's return parcel (Packeta return code)">return</span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                        p.state === 'delivered' ? 'bg-green-900/40 text-green-300'
                        : p.state === 'ready_for_pickup' ? 'bg-amber-900/40 text-amber-300'
                        : ['returned', 'returning', 'exception'].includes(p.state) ? 'bg-red-900/40 text-red-300'
                        : 'bg-blue-900/50 text-blue-300'}`}>
                        {p.state_text || (p.state || '').replace(/_/g, ' ')}
                      </span>
                      {p.expires_at && (
                        <span className="text-amber-400 text-xs">pickup by {fmtWh(p.expires_at, 'date')}</span>
                      )}
                    </div>
                    {/* the number + public page to give the customer, and (Packeta) the partner that
                        delivers it with ITS own code — parcel tracking v2, 2026-10-02 */}
                    <div className="mt-0.5 text-xs text-gray-500 flex gap-x-3 gap-y-0.5 flex-wrap">
                      <TrackLink num={p.first_mile?.number || p.tracking_number} url={p.first_mile?.url} />
                      {p.last_mile && (
                        <span>delivered by {p.last_mile.carrier || 'a partner'} · <TrackLink num={p.last_mile.number} url={p.last_mile.url} /></span>
                      )}
                    </div>
                    {/* The text WRAPS to the column (Fernando, 2026-09-10 — chosen over a
                        horizontal scroll: a scrolling strip hides exactly the part the packer
                        came for). `text` is the backend's resolution: DPD's code turned into
                        words, Packeta/Bring HTML stripped with the carrier's tracking pages
                        lifted out as `links`. */}
                    <div className="mt-1 space-y-1">
                      {(p.events || []).slice(0, allEvents[p.id] ? undefined : 5).map((e: any, i: number) => (
                        <div key={i} className="text-xs text-gray-500 flex gap-2 items-baseline">
                          <span className="w-[7.5rem] shrink-0 whitespace-nowrap tabular-nums">{fmtWh(e.event_time, 'datetime')}</span>
                          <span className="flex-1 min-w-0 break-words text-gray-400">
                            {e.text || e.description || e.code}
                            {e.location && <span className="text-gray-600"> · {e.location}</span>}
                            {(e.links || []).map((l: any, k: number) => (
                              <a key={k} href={l.href} target="_blank" rel="noopener noreferrer"
                                 className="ml-2 text-blue-400 hover:text-blue-300 whitespace-nowrap">↗ {l.label}</a>
                            ))}
                          </span>
                        </div>
                      ))}
                      {(p.events || []).length > 5 && (
                        <button onClick={() => setAllEvents(s => ({ ...s, [p.id]: !s[p.id] }))}
                          className="text-xs text-gray-500 hover:text-white">
                          {allEvents[p.id] ? 'Show the newest 5' : `Show all ${p.events.length} events`}
                        </button>
                      )}
                      {(p.events || []).length === 0 && (
                        <div className="text-xs text-gray-600">No carrier events yet</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Returns — accepted / polished / replenished / refunded (Fernando, 2026-09-16) */}
            {(detail.returns?.acceptances || []).length > 0 && (
              <div className="p-4 border-b border-gray-800">
                <div className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2">
                  Returns
                </div>
                <div className="space-y-3">
                  {detail.returns!.acceptances.map((a: any) => <ReturnAcceptanceCard key={a.id} a={a} compact onChanged={load} />)}
                </div>
              </div>
            )}

            {/* Change log */}
            <div className="p-4">
              <div className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-3">
                Message / logs from changes
              </div>
              {detail.events.length === 0 ? (
                <div className="text-gray-600 text-sm">No history yet</div>
              ) : (
                <div className="space-y-3">
                  {detail.events.map(e => (
                    <div key={e.id}>
                      <div className="text-gray-500 text-xs">{fmt(e.created_at)}</div>
                      <div className="text-sm mt-0.5">
                        {e.from_tab ? (
                          <>
                            <span className="text-gray-400">
                              {tabLabel(e.from_tab)}
                            </span>
                            <span className="text-gray-600 mx-1">→</span>
                            <span className="text-white">
                              {tabLabel(e.to_tab)}
                            </span>
                          </>
                        ) : (
                          <span className="text-white">
                            Added to {tabLabel(e.to_tab)}
                          </span>
                        )}
                        {e.username && (
                          <span className="text-gray-500 text-xs ml-1.5">by {e.username}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

           </div>

            {/* Handle order — pinned under the scroller */}
            <div className="flex-none p-4 border-t border-gray-800 space-y-2">
              <div className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2">
                Handle order
              </div>
              <button
                onClick={printDispatchNote}
                disabled={printingNote}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-white text-sm rounded-lg transition-colors"
              >
                {printingNote ? 'Generating...' : 'Print delivery note'}
              </button>
              <button
                onClick={() => setLabelMenu(true)}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg transition-colors"
              >
                Print shipping label
              </button>
              {/* Split shipment (Fernando, 2026-09-21): part now, the rest later, one order.
                  Manager/admin; not on a resend, not mid-exchange, not once split, not in a
                  terminal tab. COD orders are refused by the server (the carrier would
                  collect the amount twice). */}
              {canSplit && !detail.split && detail.kind !== 'resend' && !detail.active_exchange_id
                && detail.payment_kind !== 'cod'
                && !['archived', 'deleted', 'non_completed'].includes(detail.tab) && products.length > 0 && (
                <button
                  onClick={() => {
                    // lines waiting for a NOS supplier delivery are the natural "ship later" lines — pre-ticked
                    const pre: Record<string, number> = {};
                    products.forEach((p: any, i: number) => {
                      if (p.waiting_for_nos) pre[p.line_id || String(p.variation_id || p.product_code || i)] = p.quantity ?? 0;
                    });
                    setSplitLater(pre);
                    setSplitNote(Object.keys(pre).length ? 'waiting for the NOS supplier delivery' : '');
                    setSplitOpen(true);
                  }}
                  title="Ship part of this order now and the rest later, as two parcels of the same order"
                  className="w-full py-2 bg-gray-800 hover:bg-sky-900/60 text-sky-100 text-sm rounded-lg transition-colors"
                >
                  Split shipment
                </button>
              )}
              {/* Edit order (Fernando, 2026-10-05): manager/admin; the dialog says why when it can't */}
              {canResend && detail.kind !== 'resend' && !['archived', 'deleted'].includes(detail.tab) && (
                <button onClick={() => { setEditMsg(''); setEditOpen(true); }}
                  title="Change a line's size or quantity, or remove it — in the shop itself; the customer gets no e-mail"
                  className="w-full py-2 bg-gray-800 hover:bg-blue-900/60 text-blue-100 text-sm rounded-lg transition-colors">
                  Edit order…
                </button>
              )}
              {editMsg && <div className="text-xs text-green-300 px-1">{editMsg}</div>}
              {/* Fernando, 2026-09-09: "Create a resend order ... Right side under the
                  Print shipping label button". Manager/admin. On a resend row it
                  offers a further resend of the ORIGINAL. */}
              {/* "Send again" (Fernando 27.10 — was "Create a resend order"): Resend (manager/admin — the same items,
                  a new linked order) or Exchange (staff and up — something else instead, or the same item tied to
                  the lost line). On a resend row it opens the original. */}
              {!lookOnly && (detail.kind !== 'resend' || detail.resend_of) && (
                <button
                  onClick={() => detail.kind === 'resend' && detail.resend_of
                    ? onOpenOrder?.(detail.resend_of.order_id)
                    : (() => { setSendAgainReason(''); setSendAgain('choose'); })()}
                  title={detail.kind === 'resend' ? 'Open the original order to send again from there'
                    : 'Send this order\'s goods again: a resend (the same items) or an exchange (something else instead)'}
                  className="w-full py-2 bg-gray-800 hover:bg-amber-900/60 text-amber-100 text-sm rounded-lg transition-colors"
                >
                  {detail.kind === 'resend' ? 'Send again (from the original)' : 'Send again'}
                </button>
              )}
              {/* "Return…" (Fernando 27.10 — was the bottom card): the customer announces a return; each piece is
                  marked refund / exchange / nothing in the Accept-returns composer */}
              {!lookOnly && detail.kind !== 'resend' && (
                <button onClick={() => setReturnOpen(true)}
                  title="The customer announces a return: mark each piece for refund, exchange or nothing"
                  className="w-full py-2 bg-gray-800 hover:bg-indigo-900/60 text-indigo-100 text-sm rounded-lg transition-colors">
                  Return…
                </button>
              )}
              {/* Refund override (Fernando, 2026-09-29): what of the payment we still keep and
                  for what, and a refund of some of it — never above what was paid. Manager/admin. */}
              {canResend && (
                <button
                  onClick={() => { setRefOpen(true); setRefResult(null); setRefPos(null); setRefPick({}); setRefFree(''); setRefReason('Not the customer\'s fault'); setRefNote(''); loadRefundPosition(); }}
                  title="See what we are still keeping of this payment and refund part of it (goodwill, not the customer's fault) — never more than the customer paid"
                  className="w-full py-2 bg-gray-800 hover:bg-emerald-900/60 text-emerald-100 text-sm rounded-lg transition-colors"
                >
                  Refund…
                </button>
              )}
              {canDelete && !['archived', 'deleted'].includes(detail.tab) && detail.kind !== 'resend' && (
                <button
                  onClick={() => { setDelResult(null); setDelNote(''); setDelEmail(true); setDelOpen(true); }}
                  disabled={detail.webshop_id !== 'big_oliver' && !(detail.store_name || '').toLowerCase().includes('oliver')}
                  title={detail.webshop_id === 'big_oliver'
                    ? 'The customer cancelled: cancel the order in MyCashflow, refund the whole payment, move it to Deleted'
                    : 'Cancelling in the shop is wired for MyCashflow orders only for now — cancel it in the shop admin and move it to Deleted'}
                  className="w-full py-2 bg-red-900/60 hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed text-red-100 text-sm rounded-lg transition-colors"
                >
                  Delete order
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Refund override: the payment position and a refund of part of what is kept. */}
      {refOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6"
             onClick={() => { if (!refBusy) setRefOpen(false); }}>
          <div className="bg-[#1a1d27] border border-emerald-900/60 rounded-xl p-5 w-full max-w-lg space-y-4 max-h-[95vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white text-base font-semibold">Refund — order {detail.order_number || orderId}</h3>
                <p className="text-gray-500 text-xs mt-0.5">{detail.store_name}{refPos?.provider ? ` · paid via ${refPos.provider}` : ''}</p>
              </div>
              <button onClick={() => { if (!refBusy) setRefOpen(false); }} className="text-gray-500 hover:text-white">✕</button>
            </div>
            {refErr && <div className="rounded-lg px-3 py-2 text-sm bg-red-950/40 border border-red-900/60 text-red-200">{refErr}</div>}
            {!refPos && !refErr && <div className="text-gray-500 text-sm">Loading the payment position…</div>}
            {refPos && (
              <>
                <div className="rounded-lg border border-gray-800 bg-[#0f1117] px-3 py-2 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-400">Customer paid</span><span className="text-gray-200">{money(refPos.paid)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Refunded</span><span className="text-gray-200">-{money(refPos.refunded)}</span></div>
                  {refPos.pending > 0 && <div className="flex justify-between"><span className="text-amber-300">Refund pending{refPos.open_job ? ` (job #${refPos.open_job.id}, ${refPos.open_job.status.replace('_', ' ')})` : ''}</span><span className="text-amber-300">-{money(refPos.pending)}</span></div>}
                  <div className="flex justify-between border-t border-gray-800 pt-1"><span className="text-white font-medium">Still keeping</span><span className="text-white font-semibold">{money(refPos.kept)}</span></div>
                  {(refPos.components || []).map((c: any) => (
                    <div key={keyOf(c)} className="flex justify-between text-xs"><span className="text-gray-500 pl-3">{c.label}</span><span className="text-gray-400">{money(c.amount)}</span></div>
                  ))}
                  <div className="flex justify-between border-t border-gray-800 pt-1"><span className="text-emerald-300">Can still be refunded</span><span className="text-emerald-300 font-semibold">{money(refPos.refundable)}</span></div>
                  {refPos.payment_state === 'assumed' && <div className="text-[11px] text-amber-300">No payment record on this order — the order total is assumed paid.</div>}
                </div>
                {refResult ? (
                  <div className="rounded-lg px-4 py-3 text-sm bg-green-950/40 border border-green-900/60 text-green-200">
                    <div className="font-semibold">Refund of {money(refResult.amount)} recorded.</div>
                    <div className="mt-1">
                      {refResult.mode === 'amended' && `Added to the pending return refund (job #${refResult.job_id}) — one payment reaches the customer.`}
                      {refResult.mode === 'queued' && `Queued for Paytrail (job #${refResult.job_id}) — see Returns → Queued refunds.`}
                      {refResult.mode === 'manual' && 'Waiting in Returns → Manual refunds to be paid by hand.'}
                    </div>
                    <div className="mt-2"><button onClick={() => setRefOpen(false)} className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs">Close</button></div>
                  </div>
                ) : refPos.refundable <= 0 ? (
                  <div className="text-sm text-gray-400">Nothing left to refund: the customer has been refunded everything they paid{refPos.pending > 0 ? ' once the pending refund is paid' : ''}.</div>
                ) : (
                  <>
                    <div className="text-gray-500 text-xs uppercase tracking-wider">What to refund</div>
                    <div className="space-y-1.5 text-sm">
                      {(refPos.components || []).filter((c: any) => c.amount > 0).map((c: any) => {
                        const k = keyOf(c); const on = refPick[k] !== undefined;
                        return (
                          <label key={k} className="flex items-center gap-2 text-gray-300">
                            <input type="checkbox" checked={on}
                              onChange={e => setRefPick(p => { const n = { ...p }; if (e.target.checked) n[k] = String(c.amount); else delete n[k]; return n; })} />
                            <span className="flex-1">{c.label}</span>
                            <input type="number" step="0.01" min={0} max={c.amount} disabled={!on} value={on ? refPick[k] : ''}
                              onChange={e => setRefPick(p => ({ ...p, [k]: e.target.value }))}
                              className="keep-width w-24 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm text-right disabled:opacity-40" />
                          </label>
                        );
                      })}
                      <label className="flex items-center gap-2 text-gray-300">
                        <span className="flex-1 pl-6">Other amount</span>
                        <input type="number" step="0.01" min={0} value={refFree} onChange={e => setRefFree(e.target.value)} placeholder="0.00"
                          className="keep-width w-24 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm text-right" />
                      </label>
                    </div>
                    <div className={`flex justify-between text-sm border-t border-gray-800 pt-2 ${refTotal() > refPos.refundable + 0.001 ? 'text-red-300' : 'text-white'}`}>
                      <span>Refund total</span><span className="font-semibold">{money(refTotal())}{refTotal() > refPos.refundable + 0.001 ? ` — above the ${money(refPos.refundable)} that can still be refunded` : ''}</span>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Why (required)</div>
                      <select value={refReason} onChange={e => setRefReason(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm mb-2">
                        {['Not the customer\'s fault', 'Wrong item sent', 'Damaged / faulty on arrival', 'Goodwill', 'Other'].map(x => <option key={x} value={x}>{x}</option>)}
                      </select>
                      <input value={refNote} onChange={e => setRefNote(e.target.value)} placeholder="note — e.g. the return fee was deducted although we sent the wrong size"
                        className="w-full px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-emerald-500" />
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {refPos.open_job
                        ? `A return refund of ${money(refPos.open_job.amount)} is still on its way (job #${refPos.open_job.id}) — this is added to it, so the customer gets one payment.`
                        : refPos.auto_refund && refPos.has_transaction
                          ? 'Paid through Paytrail by the system within minutes; the order and the Queued refunds tab show it.'
                          : 'This store is not on automatic refunds (or the order has no Paytrail payment): the refund waits in Returns → Manual refunds to be paid by hand.'}
                    </p>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setRefOpen(false)} disabled={refBusy} className="px-3 py-2 rounded bg-gray-800 text-gray-300 text-sm">Cancel</button>
                      <button onClick={submitRefund} disabled={refBusy || refTotal() <= 0 || refTotal() > refPos.refundable + 0.001}
                        className="px-4 py-2 rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm font-medium">
                        {refBusy ? 'Recording…' : `Refund ${money(refTotal())}`}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete order: cancel in the shop, refund the customer, out of the flow. */}
      {delOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6"
             onClick={() => { if (!delBusy) { setDelOpen(false); if (delResult && !delResult.error) onMoved?.(); } }}>
          <div className="bg-[#1a1d27] border border-red-900/60 rounded-xl p-5 w-full max-w-lg space-y-4"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white text-base font-semibold">Are you sure you want to delete order {detail.order_number || orderId}?</h3>
                <p className="text-gray-500 text-xs mt-0.5">{detail.raw?.customer_information?.first_name} {detail.raw?.customer_information?.last_name} · {detail.store_name}</p>
              </div>
              <button onClick={() => { setDelOpen(false); if (delResult && !delResult.error) onMoved?.(); }} className="text-gray-500 hover:text-white">✕</button>
            </div>
            {delResult ? (
              <div className={`rounded-lg px-4 py-3 text-sm ${delResult.error ? 'bg-red-950/40 border border-red-900/60 text-red-200' : 'bg-green-950/40 border border-green-900/60 text-green-200'}`}>
                {delResult.error ? delResult.error : (
                  <>
                    <div className="font-semibold">Order {delResult.order_number} cancelled in MyCashflow.</div>
                    <div className="mt-1">
                      {delResult.refund?.mode === 'auto'
                        ? `Refund ${Number(delResult.refund.amount).toFixed(2)} ${delResult.refund.currency} queued for Paytrail (job #${delResult.refund.job_id}) — see Returns → Queued refunds.`
                        : `Refund ${Number(delResult.refund?.amount || 0).toFixed(2)} ${delResult.refund?.currency || ''} is waiting in Returns → Manual refunds to be paid by hand.`}
                      {' '}Cancellation e-mail {delResult.email_sent ? 'sent' : 'not sent'}.
                    </div>
                    <div className="mt-1 text-xs">
                      {delResult.recover === 'none'
                        ? (delResult.stock?.length ? 'Stock not recovered — deducted again:' : 'Stock not recovered — nothing to deduct.')
                        : (delResult.stock?.length ? "Stock recovered, except the can't-find pieces deducted again:" : 'Stock recovered — the shop\'s release stands for every line.')}
                      {(delResult.stock || []).map((x: any, i: number) => (
                        <div key={i} className={x.ok ? 'text-green-200' : 'text-red-300'}>{x.ok ? '✓' : '⚠'} {x.qty}× {x.line}{x.target ? ` → ${x.target}` : ''}{!x.ok && x.reason ? ` — ${x.reason}` : ''}</div>
                      ))}
                    </div>
                    <div className="mt-2">
                      <button onClick={() => { setDelOpen(false); onMoved?.(); }}
                        className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs">Close</button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="text-sm text-gray-300 space-y-2">
                  <p>This cancels the order in MyCashflow and refunds the customer the whole payment:
                    <span className="text-white font-semibold ml-1">{money(detail.amount)}</span>
                    {detail.payment_method && <span className="text-gray-500"> · {detail.payment_method}</span>}.
                    The refund is paid through Paytrail by the system when the store is on automatic refunds, otherwise it waits in Manual refunds.</p>
                  {(detail.labels || []).some(l => !l.cancelled_at) && (
                    <p className="text-amber-300 text-xs">⚠ This order has a live shipping label — it will be cancelled (or flagged for the carrier portal).</p>
                  )}
                  {['printed', 'picker_2', 'hard_to_find', 'cant_find', 'label_issue'].includes(detail.tab) && (
                    <p className="text-amber-300 text-xs">⚠ The order is in picking — make sure it is not packed and on its way.</p>
                  )}
                  {detail.active_exchange_id && (
                    <p className="text-amber-300 text-xs">⚠ An exchange pass is running on this order — it will be aborted; the exchange stays open in Returns → Exchange orders.</p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={delEmail} onChange={e => setDelEmail(e.target.checked)} />
                  Send MyCashflow&apos;s cancellation e-mail to the customer
                </label>
                <div>
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Why (required)</div>
                  <input value={delNote} onChange={e => setDelNote(e.target.value)} placeholder="e.g. customer asked to cancel by e-mail 17.9."
                    className="w-full px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-red-500" />
                </div>
                {/* the lines, with what the warehouse could not find — that is what "recover" is about */}
                {(() => {
                  const nfQty: Record<string, number> = detail.not_found_qty || {};
                  const nfSet = new Set((detail.not_found_items || []).map(String));
                  const rows = (products || []).map((p: any, i: number) => {
                    const lid = lineIdOf(p, i);
                    const qty = Number(p.quantity || 0);
                    const missing = Math.min(qty, nfQty[lid] ?? (nfSet.has(lid) ? qty : 0));
                    return { lid, name: titleEn(p), qty, missing };
                  });
                  const missingTotal = rows.reduce((n: number, r: any) => n + r.missing, 0);
                  return (
                    <div className="rounded-lg border border-gray-800 bg-[#0f1117] px-3 py-2 text-xs space-y-1">
                      {rows.map((r: any) => (
                        <div key={r.lid} className="flex justify-between gap-3">
                          <span className="text-gray-300 truncate">{r.qty}× {r.name}</span>
                          {r.missing > 0 && <span className="text-red-300 whitespace-nowrap">can't find ×{r.missing}</span>}
                        </div>
                      ))}
                      <div className="text-gray-500 pt-1 border-t border-gray-800">
                        <span className="text-green-300">Recover stock balance</span> puts every line back into stock
                        {missingTotal > 0 ? <> except the can&apos;t-find pieces ({missingTotal}), which are deducted again automatically.</> : '.'}
                        {' '}<span className="text-red-300">Without recovery</span> deducts every line again.
                      </div>
                    </div>
                  );
                })()}
                <div className="flex justify-end gap-2 flex-wrap">
                  <button onClick={() => setDelOpen(false)} disabled={delBusy}
                    className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm">No, abort</button>
                  <button onClick={() => submitDelete('none')} disabled={delBusy || !delNote.trim()}
                    title="Cancel and refund; every line's released quantity is deducted again (the goods are not on the shelf)"
                    className="px-4 py-2 rounded-lg bg-red-800 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold">
                    {delBusy ? 'Cancelling…' : 'Yes, without recovery'}
                  </button>
                  <button onClick={() => submitDelete('all')} disabled={delBusy || !delNote.trim()}
                    title="Cancel and refund; the goods count as stock again — except pieces marked can't find on this order"
                    className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm font-semibold">
                    {delBusy ? 'Cancelling…' : 'Yes, recover stock balance'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Split shipment: which lines (and how many of each) ship LATER; the rest ships now. */}
      {splitOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6" onClick={() => setSplitOpen(false)}>
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-5 w-full max-w-2xl space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-white text-base font-semibold">Split shipment</h3>
              <button onClick={() => setSplitOpen(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <p className="text-gray-400 text-sm">
              Set how many of each line ship <b>later</b>. Everything else ships now as parcel 1; the later
              lines wait as parcel 2 until you release them — or until the shop moves the order out of Waiting for
              NOS-product (the NOS item was inbounded): then parcel 2 releases itself. A shipping label already bought stays with parcel 1.
              {!['new', 'printed', 'picker_2'].includes(detail.tab) && <> The order moves to <b>New</b> so parcel 1 can be picked.</>}
              {' '}The shop is told once, when the last parcel has shipped.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-3 py-1.5 text-left">Article #</th>
                  <th className="px-3 py-1.5 text-left">Product</th>
                  <th className="px-3 py-1.5 text-center">Ordered</th>
                  <th className="px-3 py-1.5 text-center">Ship later</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p: any, i: number) => {
                  const lid = p.line_id || String(p.variation_id || p.product_code || i);
                  const q = p.quantity ?? 0;
                  return (
                    <tr key={lid} className="border-b border-gray-800/60">
                      <td className="px-3 py-1.5 font-mono text-xs text-gray-400">{p.article_number || p.product_code}</td>
                      <td className="px-3 py-1.5 text-white" title={customerWords(p)}>{titleEn(p)}{p.model_name ? <span className="text-gray-400 text-xs ml-1.5">{p.model_name}</span> : null}
                        {p.waiting_for_nos && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-700 text-white align-middle" title="Waiting for the NOS supplier delivery — ships later">NOS</span>}
                      </td>
                      <td className="px-3 py-1.5 text-center text-gray-300">{q}</td>
                      <td className="px-3 py-1.5 text-center">
                        <input type="number" min={0} max={q} value={splitLater[lid] ?? 0}
                          onChange={e => setSplitLater(s => ({ ...s, [lid]: Math.max(0, Math.min(q, Number(e.target.value) || 0)) }))}
                          className="w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-center text-white" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <input value={splitNote} onChange={e => setSplitNote(e.target.value)} placeholder="Why (optional) — e.g. size L arrives next week"
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-600" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setSplitOpen(false)} className="px-3 py-1.5 text-sm rounded bg-gray-800 hover:bg-gray-700 text-gray-300">Cancel</button>
              <button onClick={submitSplit} disabled={splitBusy} className="px-3 py-1.5 text-sm rounded bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-40">
                {splitBusy ? 'Splitting…' : 'Split'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <OrderEditDialog orderId={orderId} orderNumber={String(detail.order_number || orderId)}
          onClose={() => setEditOpen(false)}
          onDone={msg => { setEditOpen(false); setEditMsg(`✓ ${msg}`); load(); onChanged?.(); }} />
      )}
      {/* Send again (Fernando 27.10): the original parcel's facts first, then Resend or Exchange — the person chooses */}
      {sendAgain === 'choose' && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6" onClick={() => setSendAgain(null)}>
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-5 w-full max-w-2xl space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-white text-base font-semibold">Send again — {detail.order_number || orderId}</h3>
              <button onClick={() => setSendAgain(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            <TrackingFacts s={trackSummary} />
            <div className="grid gap-3 md:grid-cols-2">
              <button disabled={!canResend}
                onClick={() => { setSendAgain(null); openResend(); }}
                className="text-left rounded-lg border border-amber-900/60 bg-amber-950/20 hover:bg-amber-900/30 disabled:opacity-40 disabled:hover:bg-amber-950/20 p-4">
                <div className="text-amber-100 font-semibold">Resend</div>
                <div className="text-xs text-gray-300 mt-1">The <b>same items</b> again — a new linked order {detail.order_number || orderId}-R{(detail.resends || []).length + 1}.
                  For a lost parcel or missing items when nothing changes.</div>
                {!canResend && <div className="text-xs text-gray-500 mt-1">Managers and admins.</div>}
              </button>
              <button disabled={!!detail.active_exchange_id}
                onClick={() => setSendAgain('exchange')}
                className="text-left rounded-lg border border-indigo-900/60 bg-indigo-950/20 hover:bg-indigo-900/30 disabled:opacity-40 disabled:hover:bg-indigo-950/20 p-4">
                <div className="text-indigo-100 font-semibold">Exchange</div>
                <div className="text-xs text-gray-300 mt-1"><b>Something else instead</b> — another size or colour — or the same item, tied to
                  the line it replaces. When the original turns up, it is not refunded.</div>
                {detail.active_exchange_id && <div className="text-xs text-gray-500 mt-1">An exchange is already running on this order.</div>}
              </button>
            </div>
            <p className="text-xs text-gray-500">The reason comes next (parcel lost, items missing from the parcel, defect …). A customer who has not paid
              (a lost cash-on-delivery parcel) pays on the new label what they agreed to — never more.</p>
          </div>
        </div>
      )}
      {sendAgain === 'exchange' && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6" onClick={() => setSendAgain(null)}>
          <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto space-y-3" onClick={e => e.stopPropagation()}>
            <TrackingFacts s={trackSummary} />
            <ExchangeComposer orderId={orderId} products={products} webshopId={detail.webshop_id}
              sendAgain={{ reason: sendAgainReason || 'Lost by carrier' }}
              onCreated={() => { load(); loadParcels(); }} onClose={() => setSendAgain(null)} />
          </div>
        </div>
      )}
      {returnOpen && <ReturnDialog orderId={orderId} onChanged={load} onClose={() => setReturnOpen(false)} />}

      {/* Create a resend order: which lines, how many, why. */}
      {resendOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6"
             onClick={() => { if (!resendBusy) setResendOpen(false); }}>
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-5 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white text-base font-semibold">Send again — resend</h3>
                <p className="text-gray-500 text-xs mt-0.5">
                  A new order <span className="text-gray-300">{detail.order_number || orderId}-R{(detail.resends || []).length + 1}</span> appears in New,
                  linked to this one, and is picked, packed and labelled like any order. The items are reserved
                  in {detail.store_name || 'the store'}&apos;s stock system now.
                </p>
              </div>
              <button onClick={() => setResendOpen(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>

            {resendResult ? (
              <div className={`rounded-lg px-4 py-3 text-sm ${resendResult.error ? 'bg-red-950/40 border border-red-900/60 text-red-200' : 'bg-green-950/40 border border-green-900/60 text-green-200'}`}>
                {resendResult.error ? resendResult.error : (
                  <>
                    <div className="font-semibold">Resend {resendResult.order_number} created{resendResult.reserved ? ' — stock reserved.' : ' — NOT every item could be reserved:'}</div>
                    {!resendResult.reserved && (
                      <ul className="mt-1 list-disc list-inside text-xs">
                        {(resendResult.reserve_results || []).filter((x: any) => !x.ok).map((x: any, i: number) => (
                          <li key={i}>{x.line}: {x.reason}</li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => { setResendOpen(false); onOpenOrder?.(resendResult.id); }}
                        className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs">Open {resendResult.order_number}</button>
                      <button onClick={() => setResendOpen(false)}
                        className="px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs">Stay here</button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="rounded-lg border border-teal-900/60 bg-teal-950/30 px-3 py-2 text-xs text-teal-100">
                  Faulty item, same item again?{' '}
                  {detail.kind !== 'resend' && !detail.active_exchange_id ? (
                    <button onClick={() => { setResendOpen(false); setSendAgainReason('Defect'); setSendAgain('exchange'); }}
                      className="underline decoration-dotted font-semibold hover:text-white">Use Send again → Exchange (same item)</button>
                  ) : <span className="font-semibold">Use Send again → Exchange (same item)</span>}
                  {' '}— the same order goes out again, nothing is charged, and you can tick that the customer keeps the faulty one.
                  A resend with reason <span className="font-semibold">Defect</span> also works when a separate order is needed — untick the charges below.
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-800">
                      <th className="text-left py-1.5 w-8"></th>
                      <th className="text-left">Item</th>
                      <th className="text-right">Ordered</th>
                      <th className="text-right">Re-send</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p: any, i: number) => {
                      const lid = lineIdOf(p, i);
                      const q = resendPick[lid] || 0;
                      const max = Number(p.quantity || 0);
                      return (
                        <tr key={lid} className="border-b border-gray-800/60">
                          <td className="py-1.5">
                            <input type="checkbox" className="accent-amber-500" checked={q > 0}
                              onChange={e => setResendPick(s => ({ ...s, [lid]: e.target.checked ? max : 0 }))} />
                          </td>
                          <td className="text-gray-200" title={customerWords(p)}>{titleEn(p)}
                            <span className="text-gray-600 text-xs ml-2 font-mono">{p.product_code}</span></td>
                          <td className="text-right text-gray-400">{max}</td>
                          <td className="text-right">
                            <input type="number" min={0} max={max} value={q}
                              onChange={e => setResendPick(s => ({ ...s, [lid]: Math.max(0, Math.min(max, Number(e.target.value) || 0)) }))}
                              className="w-16 bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded px-2 py-1 text-right" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-500 text-xs uppercase tracking-wider">Reason</label>
                    <select value={resendReasonId} onChange={e => setResendReasonId(e.target.value ? Number(e.target.value) : '')}
                      className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2">
                      <option value="">— choose —</option>
                      {resendReasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs uppercase tracking-wider">Details (free text)</label>
                    <input value={resendReasonText} onChange={e => setResendReasonText(e.target.value)}
                      placeholder="e.g. Omniva case 12345, customer confirmed by email"
                      className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-500 text-xs uppercase tracking-wider">Internal note (optional)</label>
                  <input value={resendNote} onChange={e => setResendNote(e.target.value)}
                    className="mt-1 w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2" />
                </div>
                <label className="flex items-start gap-2 text-sm text-gray-200 cursor-pointer">
                  <input type="checkbox" className="accent-teal-500 mt-0.5" checked={resendKeeps} onChange={e => setResendKeeps(e.target.checked)} />
                  <span>
                    The customer keeps the original — we don&apos;t expect it back
                    <span className="block text-xs text-gray-500">
                      Logged on both orders. If it comes back anyway it is not refunded; with reason Defect or Damaged in transit it is held off sale for a manager to decide.
                    </span>
                  </span>
                </label>

                {(() => {
                  const lines = products;
                  const chosen = lines.filter((p: any, i: number) => (resendPick[lineIdOf(p, i)] || 0) > 0);
                  const full = chosen.length === lines.length && lines.every((p: any, i: number) => (resendPick[lineIdOf(p, i)] || 0) === Number(p.quantity || 0));
                  const grossAll = lines.reduce((s: number, p: any) => s + Number(p.unit_price || 0) * Number(p.quantity || 0), 0);
                  const paidChosen = lines.reduce((s: number, p: any, i: number) => {
                    const q = resendPick[lineIdOf(p, i)] || 0;
                    const oq = Number(p.quantity || 0) || 1;
                    const unit = Number(p.unit_price || 0) - Number(p.line_discount || 0) / oq;
                    return s + unit * q;
                  }, 0);
                  const grossChosen = lines.reduce((s: number, p: any, i: number) => s + Number(p.unit_price || 0) * (resendPick[lineIdOf(p, i)] || 0), 0);
                  const disc = full ? discount : (grossAll ? discount * grossChosen / grossAll : 0);
                  // the same arithmetic as resends.resend_raw: each row is the parent's figure or 0
                  const itemsNet = Math.max(0, paidChosen - disc);
                  const feeParent = Number(paymentFees || paymentCosts || 0);
                  const total = (charge.items ? itemsNet : 0) + (charge.shipping ? Number(shippingCost || 0) : 0) + (charge.fee ? feeParent : 0);
                  const isCod = detail.payment_kind === 'cod';
                  const codZero = isCod && total <= 0;
                  const Row = ({ k, label, value }: { k: 'items' | 'shipping' | 'fee'; label: string; value: number }) => (
                    <label className={`flex items-center justify-between gap-3 px-3 py-1.5 rounded cursor-pointer ${charge[k] ? 'text-gray-200' : 'text-gray-500 line-through'}`}>
                      <span className="flex items-center gap-2">
                        <input type="checkbox" className="accent-amber-500" checked={charge[k]}
                          onChange={e => setCharge(c => ({ ...c, [k]: e.target.checked }))} />
                        {label}
                      </span>
                      <span className="font-mono">{money(value)}</span>
                    </label>
                  );
                  return (
                    <div className="text-xs bg-gray-900/60 border border-gray-800 rounded-lg px-2 py-2 space-y-1">
                      <div className="px-3 text-gray-500 uppercase tracking-wider text-[10px]">
                        Charge again — a general resend recharges everything; untick what this one should not charge
                      </div>
                      <Row k="items" label={full ? 'Items (everything re-sent)' : `Items (${chosen.length} of ${lines.length} lines)`} value={itemsNet} />
                      <Row k="shipping" label="Shipping (as on the original)" value={Number(shippingCost || 0)} />
                      <Row k="fee" label="Payment fee (as on the original)" value={feeParent} />
                      <div className="flex items-center justify-between px-3 pt-1 border-t border-gray-800 text-gray-200">
                        <span>Total the resend charges</span>
                        <span className="font-mono font-semibold">{money(total)}</span>
                      </div>
                      {isCod ? (
                        <div className={`px-3 ${codZero ? 'text-red-300' : 'text-amber-200'}`}>
                          Cash on delivery ({detail.payment_method}): {codZero
                            ? 'this resend would collect nothing at the door — a COD parcel cannot be labelled like that. Charge at least one part, or settle payment with the customer another way.'
                            : <>the resend collects <span className="font-semibold">{money(total)}</span> at the door.</>}
                        </div>
                      ) : detail.payment_kind === 'prepaid' ? (
                        <div className="px-3 text-gray-400">Prepaid order ({detail.payment_method}): nothing is collected at the door; the total is the value on the note and for customs.</div>
                      ) : (
                        <div className="px-3 text-gray-400">Payment method {detail.payment_method || 'unknown'} is not classified for this store — the label code decides COD at print time.</div>
                      )}
                      <div className="px-3 text-gray-400">Stock: −{chosen.reduce((s: number, p: any, i: number) => s + (resendPick[lineIdOf(p, i)] || 0), 0)} pcs reserved in {detail.store_name || 'the store'}&apos;s system now; released if the resend is cancelled.</div>
                    </div>
                  );
                })()}

                <div className="flex justify-end gap-2">
                  <button onClick={() => setResendOpen(false)} className="px-3 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
                  <button onClick={submitResend} disabled={resendBusy}
                    className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40">
                    {resendBusy ? 'Creating…' : 'Create resend order'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Print shipping label: Order label (the order's own data, the same job
          flow as the grid) or Custom (hand-set fields on a prefilled form). */}
      {labelMenu && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 md:p-6"
             onClick={() => setLabelMenu(false)}>
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-5 w-full max-w-md space-y-4"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-white text-base font-semibold">Print shipping label</h3>
              <button onClick={() => setLabelMenu(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            {/* What Order label will do is the server's answer (live_label_id), not a
                guess from the label list: on an archived order the labels the parcel
                left with are spent, and printing again buys a new one. */}
            {detail?.split?.between ? (
              <div className="text-amber-200 text-sm bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2">
                Split shipment between parcels: parcel {detail.split.parcels.filter(p => p.status === 'dispatched').map(p => p.parcel_no).join(', ') || '—'} has left
                and parcel {detail.split.parcels.filter(p => p.status === 'open').map(p => p.parcel_no).join(', ')} is not released yet.
                There is nothing to label — release the next parcel in the Shipments card first.
              </div>
            ) : detail?.live_label_id ? (
              <div className="text-gray-400 text-sm bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
                This order has a stored label
                {' '}({(detail.labels || []).find(l => l.id === detail.live_label_id)?.tracking_number || '#' + detail.live_label_id}).
                Order label will print that stored label — no new shipment is bought. If the order changed since, the run will say so.
              </div>
            ) : detail?.tab === 'archived' && (detail.labels || []).some(l => !l.cancelled_at) ? (
              <div className="text-amber-200 text-sm bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2">
                This order was dispatched{detail.dispatched_at ? ` on ${fmtWh(detail.dispatched_at, 'short')}` : ''} with label
                {' '}{(detail.labels || []).filter(l => !l.cancelled_at).map(l => l.tracking_number).join(', ')}.
                Order label will buy a <b>new</b> label — the old one left with the parcel.
              </div>
            ) : null}
            <button onClick={startOrderLabel} disabled={startingRun || !!detail?.split?.between}
              className="w-full text-left px-4 py-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 rounded-lg transition-colors">
              <div className="text-white text-sm font-medium">{startingRun ? 'Starting…' : 'Order label'}</div>
              <div className="text-gray-500 text-xs mt-0.5">
                {detail?.split?.between
                  ? 'Not available between parcels — release the next parcel first.'
                  : detail?.live_label_id
                  ? 'Prints the stored label — no new shipment.'
                  : detail?.split?.active_parcel_no
                  ? `Buys a new label for parcel ${detail.split.active_parcel_no} (its lines, its weight) and prints it.`
                  : 'Buys a new label from the order\'s own data and prints it.'}
              </div>
            </button>
            <button onClick={() => navigate(`/orders/${orderId}/custom-label`)}
              className="w-full text-left px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors">
              <div className="text-white text-sm font-medium">Custom…</div>
              <div className="text-gray-500 text-xs mt-0.5">A prefilled form where anything can be changed — pickup point, address, even the carrier. Attaches to the order.</div>
            </button>
          </div>
        </div>
      )}

      {labelRun && (
        <LabelRunPanel
          jobId={labelRun.jobId}
          zebra={labelRun.zebra}
          onClose={() => { setLabelRun(null); load(); onChanged?.(); }}
          onPrinted={() => { load(); onChanged?.(); }}
        />
      )}
    </div>
  );
}
